import {
  keepPreviousData,
  useIsMutating,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { reportFailure } from '../observability/report';
import { applySubmissionToStanding } from './staff/submissionCycle';
import type { PortalWalkTrace } from './staff/portalWalk';

import {
  ApiError,
  approveReviewCycle,
  approveReviewItem,
  buildCyclePreview,
  call,
  callAttachment,
  cancelDelta,
  correctReviewItemDate,
  createCompany,
  createGroup,
  createContract,
  createInsurer,
  createUser,
  CycleStage,
  OffCycleNoticeStatus,
  deactivateUser,
  CompanyListSort,
  type CompanyGroupListSort,
  DeltaStatus,
  SortDirection,
  discardMailMessage,
  downloadBaselineImportSource,
  downloadConsolidatedFile,
  fetchConsolidatedStanding,
  markConsolidatedFailed,
  markConsolidatedSent,
  prepareConsolidated,
  downloadGroupFile,
  fetchGroupFileStanding,
  markGroupFileFailed,
  markGroupFileSent,
  prepareGroupFile,
  downloadNoticeFile,
  downloadSnapshotFile,
  fetchCyclePreview,
  fetchSnapshot,
  fetchEligibleMovements,
  fetchHealth,
  fetchLateArrivals,
  fetchMovementStatus,
  fetchReviewCycle,
  fetchReviewQueue,
  fetchSubmissionCalendar,
  fetchSubmissionHistory,
  fetchSubmissionStanding,
  fetchUpcomingMovements,
  getMailMessage,
  importBaseline,
  importCompanyBaseline,
  importGroupBaseline,
  getPersonHistory,
  lookUpCompanyInsuredPerson,
  issueMachineClient,
  linkDocument,
  listBaselineImports,
  listCompanies,
  rememberSelectedCompany,
  rememberTableColumns,
  listGroups,
  getGroup,
  renameGroup,
  setGroupFileNameLabel,
  markAllNotificationsRead,
  markNotificationRead,
  listContracts,
  listDeltas,
  listDocuments,
  listInsurers,
  listSnapshotLayouts,
  listMachineClients,
  listMailboxSenders,
  listMailMessages,
  listNotifications,
  listOffCycleNotices,
  listAuditEntries,
  listCertificates,
  listUsers,
  markNoticeFailed,
  markNoticeSent,
  markSubmissionFailed,
  markSubmissionSent,
  pollMailbox,
  prepareSubmission,
  projectCompanyRoster,
  projectGroupRoster,
  projectRoster,
  raiseOffCycleNotice,
  registerMailboxSender,
  rejectReviewItem,
  reopenReviewCycle,
  retryMailMessage,
  returnReviewItem,
  revokeMachineClient,
  revokeMailboxSender,
  runEsocialDiagnostics,
  fetchEsocialEvents,
  confirmEsocialFetch,
  readEsocialPortalBrowser,
  runEsocialPortalWalk,
  listEsocialPortalWalks,
  fetchEsocialPortalWalk,
  downloadEsocialPortalWalkEvidence,
  downloadEsocialPortalWalkActionScreenshot,
  downloadEsocialPortalWalkBundle,
  runEsocialPortalSession,
  listEsocialPortalSessions,
  fetchEsocialPortalSession,
  listEsocialEvents,
  listEsocialFetches,
  setCompanyCnpj,
  setCompanyFileNameLabel,
  setCompanyTerminationDocumentPolicy,
  setCompanyGroup,
  rotateMachineClient,
  submitDelta,
  updateContract,
  deactivateCertificate,
  uploadCertificate,
  uploadDocument,
  type A1CertificateResponse,
  type AuditAction,
  type BaselineImportListResponse,
  type BaselineImportResponse,
  type CompanyBaselineImportResponse,
  type GroupBaselineImportResponse,
  type CompanyCreate,
  type CompanyGroupCreate,
  type CompanyGroupDetailResponse,
  type CompanyResponse,
  type ContractCreate,
  type ContractResponse,
  type ContractUpdate,
  type CycleApprovalResponse,
  type CyclePreviewResponse,
  type DeltaCreate,
  type DeltaListResponse,
  type DeltaResponse,
  type DocumentKind,
  type DocumentKind as MailboxDocumentKind,
  type EsocialDiagnosticResponse,
  type EsocialEventType,
  type EsocialFetchCommitResponse,
  type EsocialFetchResultResponse,
  type EsocialPortalWalkRequest,
  type EsocialPortalSessionRequest,
  type EsocialPortalSessionResponse,
  type EsocialPortalBrowserResponse,
  type EsocialPortalWalkResponse,
  type InsurerCreate,
  type InsurerResponse,
  type InsuredPersonLookupResponse,
  type LookupContractEntry,
  type PersonHistoryResponse,
  type MachineClientCreate,
  type MachineClientCreatedResponse,
  type MailboxPollResponse,
  type MailboxSenderResponse,
  type MailMessageDetailResponse,
  type MailMessageResponse,
  type NotificationResponse,
  type NotificationsReadResponse,
  type NoticeResponse,
  type NoticeListResponse,
  type EligibleMovementListResponse,
  type SnapshotDetailResponse,
  type SubmissionChannel,
  type SubmissionResponse,
  type SubmissionCalendarResponse,
  type ConsolidatedSubmissionResponse,
  type GroupSubmissionResponse,
  type CycleStandingResponse,
  type TerminationDocumentPolicy,
  type UserCreate,
  type UserResponse,
  type Attachment,
} from '../api';
import type { AsyncState } from '../components';

/**
 * The query keys, and the hooks that read through them.
 *
 * They are here, and not inline in a component, because a query key is shared
 * state: two components inventing `['companies']` separately is how a cache
 * stops invalidating correctly. One module owns the keys.
 *
 * Every one of them calls a generated function through `call`, which is the
 * only path to the backend that exists.
 */
export const QUERY_KEYS = {
  health: ['health'] as const,
  companies: ['companies'] as const,
  // One page of the staff list (#427). Under `['companies']` so every write
  // that invalidates the registry refetches the page on screen too; keyed on
  // the search, the page, the column filters and the sort (#439) so each one
  // caches on its own and going back to a view already seen is instant.
  companiesPage: (search: string, page: number, listing: CompaniesListing) =>
    ['companies', 'page', search, page, listing.filters ?? {}, listing.sort ?? null] as const,
  // Economic groups. The list and each group's detail share the root, so one
  // invalidation of `['groups']` refetches both -- which is what a membership
  // change needs, since it moves a count on the list and a row on a detail.
  // Its own root rather than a branch of `['companies']` for the reason
  // `companyContracts` gives below: registering a company has no reason to
  // refetch every group.
  groups: ['groups'] as const,
  // One page of the staff groups list (#440). Under `['groups']`, so every
  // membership write that invalidates the registry refetches the page on
  // screen too, and keyed on everything the request carries.
  groupsPage: (view: GroupsPageView) =>
    [
      'groups',
      'page',
      view.name,
      view.slug,
      view.sort?.column ?? null,
      view.sort?.desc ?? false,
      view.page,
    ] as const,
  group: (groupId: string) => ['groups', groupId] as const,
  // Under the group's own key, so the membership writes that invalidate
  // `['groups']` -- a company in or out changes who is read -- refetch it.
  groupRoster: (groupId: string, asOf: string) => ['groups', groupId, 'roster', asOf] as const,
  contracts: ['contracts'] as const,
  machineClients: ['machine-clients'] as const,
  // The platform's own A1 certificates. Its own root rather than a branch of
  // `machine-clients`: they are both credentials and that is the whole of what
  // they share -- one is issued to a partner, the other is what we authenticate
  // as, and installing one has no reason to refetch the other.
  certificates: ['certificates'] as const,
  // Insurers belong to no company: one list, shared by every contract form.
  insurers: ['insurers'] as const,
  // The layouts the server has registered: code-defined, so they change with a
  // deploy and never with anything done on a screen.
  snapshotLayouts: ['snapshot-layouts'] as const,
  // A company's own contracts, keyed by the company, because the staff screen
  // reads them one company at a time and `['contracts']` unqualified is the
  // *principal's* scope -- which for staff is every company there is.
  //
  // Its own root rather than `['companies', id, 'contracts']`: invalidation
  // matches by prefix, so hanging these under the companies key would mean
  // registering a company silently refetched every open company's contracts.
  companyContracts: (companyId: string) => ['company-contracts', companyId] as const,
  // One segurado of one company, read by staff (#418). Its own root rather than
  // the portal's `person-history` key: the portal's is keyed by the customer's
  // selected company and its single contract, and a staff page that shared it
  // would be one invalidation away from reading the other area's answer. The
  // lookup is keyed by the row in the URL and never by the CPF it resolves to.
  staffPersonHistory: (companyId: string, contractId: string, personId: string) =>
    ['staff-person', companyId, 'history', contractId, personId] as const,
  staffPersonLookup: (companyId: string, personId: string) =>
    ['staff-person', companyId, 'lookup', personId] as const,
  // The baseline imports run against one contract. Keyed by the contract and
  // not by the company, because "which population did this contract start
  // from?" is a question about the contract -- a company with two contracts has
  // two different answers and one key would hand the second one the first's.
  baselineImports: (contractId: string) => ['baseline-imports', contractId] as const,
  // The review queue's keys are parameterised by the cycle, because "April's
  // queue" and "May's queue" are different answers and a cache that conflated
  // them would show an analyst last month's work.
  reviewQueue: (cycleMonth: string) => ['review', 'queue', cycleMonth] as const,
  reviewCycle: (contractId: string, cycleMonth: string) =>
    ['review', 'cycle', contractId, cycleMonth] as const,
  reviewUpcoming: (contractId: string, cycleMonth: string) =>
    ['review', 'upcoming', contractId, cycleMonth] as const,
  reviewLateArrivals: (contractId: string, cycleMonth: string) =>
    ['review', 'late-arrivals', contractId, cycleMonth] as const,
  reviewPreview: (contractId: string, cycleMonth: string) =>
    ['review', 'preview', contractId, cycleMonth] as const,
  // One snapshot with its rows, keyed by its digest as well as its id: a draft
  // rebuilt in place keeps its id and changes its contents, and a key on the id
  // alone would go on serving the rows of the file it replaced. Outside
  // `['review']` because the digest already says when the answer changes, so a
  // decision has no reason to refetch it.
  snapshot: (snapshotId: string, sha256: string) => ['snapshot', snapshotId, sha256] as const,
  // The submission screen's keys are parameterised by the cycle for the same
  // reason the review queue's are, and they sit under their own root so that
  // marking a month sent can invalidate the rhythm without discarding the
  // analyst's half-read review queue.
  submissionCalendar: (cycleMonth: string) => ['submissions', 'calendar', cycleMonth] as const,
  submissionStanding: (contractId: string, cycleMonth: string) =>
    ['submissions', 'standing', contractId, cycleMonth] as const,
  submissionHistory: (contractId: string) => ['submissions', 'history', contractId] as const,
  consolidatedStanding: (companyId: string, insurerId: string, cycleMonth: string) =>
    ['submissions', 'consolidated', companyId, insurerId, cycleMonth] as const,
  groupFileStanding: (groupId: string, insurerId: string, cycleMonth: string) =>
    ['submissions', 'group', groupId, insurerId, cycleMonth] as const,
  // The off-cycle route hangs off its own root rather than under
  // `['submissions']`, because it is an exception beside the monthly rhythm
  // and not a stage of it: raising a notice changes neither the calendar nor
  // any month's stage, and invalidating the rhythm from here would refetch a
  // page of contracts that cannot have moved.
  offCycleEligible: (contractId: string) => ['off-cycle', 'eligible', contractId] as const,
  offCycleNotices: (contractId: string) => ['off-cycle', 'notices', contractId] as const,
  // The mail bridge. The log's key carries the filter because "tudo" and "o que
  // falta" are different answers, and a cache that conflated them would show an
  // analyst a queue that is not the one they asked for.
  mailMessages: (outstandingOnly: boolean) => ['mailbox', 'messages', outstandingOnly] as const,
  mailMessage: (messageId: string, attempt: number | null) =>
    ['mailbox', 'messages', messageId, attempt] as const,
  mailboxSenders: ['mailbox', 'senders'] as const,
  // The eSocial intake. Both keys carry the company they were narrowed to,
  // because "every company's staged events" and "this one's" are different
  // answers to the same URL -- and unlike the mailbox, these tables are
  // company-scoped, so the narrowing is real rather than cosmetic. `'todas'`
  // rather than an empty string so an unnarrowed list is legible in devtools.
  esocialEvents: (companyId: string | null, eventType: EsocialEventType | null) =>
    ['esocial', 'events', companyId ?? 'todas', eventType ?? 'todos'] as const,
  esocialFetches: (companyId: string | null) =>
    ['esocial', 'fetches', companyId ?? 'todas'] as const,
  // The portal walk (#452). Identifiers only -- a company, a run, a stage
  // index -- never a filter value, so no CPF a walk was asked about can reach a
  // key.
  esocialPortalBrowser: ['esocial', 'portal-browser'] as const,
  esocialPortalWalks: (companyId: string | null) =>
    ['esocial', 'portal-walks', 'list', companyId ?? 'todas'] as const,
  esocialPortalWalk: (runId: string) => ['esocial', 'portal-walks', 'run', runId] as const,
  esocialPortalWalkScreenshot: (runId: string, index: number) =>
    ['esocial', 'portal-walks', 'screenshot', runId, index] as const,
  esocialPortalWalkActionScreenshot: (runId: string, index: number, action: number) =>
    ['esocial', 'portal-walks', 'action-screenshot', runId, index, action] as const,
  esocialPortalWalkTrace: (runId: string, index: number) =>
    ['esocial', 'portal-walks', 'trace', runId, index] as const,
  // Session walks (#479), by their own identifier.
  esocialPortalSessions: ['esocial', 'portal-sessions', 'list'] as const,
  esocialPortalSession: (sessionId: string) =>
    ['esocial', 'portal-sessions', 'run', sessionId] as const,
  // The trail's key carries the whole filter, the page included. Two filters
  // are two different answers to "what happened?", and a key that held only
  // `['audit']` would hand the second question the first one's page for as long
  // as it stayed fresh -- on a screen whose entire purpose is to be believed.
  auditEntries: (filters: AuditEntryFilters) => ['audit', 'entries', filters] as const,
  // The people who sign in. Keyed by the company the list was narrowed to,
  // because "every user staff can see" and "this company's users" are
  // different answers and the backend gives them to the same URL -- the filter
  // travels as a header, which a cache does not see. `'todas'` rather than an
  // empty string so an unnarrowed list is legible in the devtools.
  users: (companyId: string | null, groupId: string | null = null) =>
    ['users', companyId ?? 'todas', groupId ?? 'todos-os-grupos'] as const,
};

export function useHealth() {
  return useQuery({
    queryKey: QUERY_KEYS.health,
    queryFn: () => call(fetchHealth()),
  });
}

export function useCompanies() {
  return useQuery({
    queryKey: QUERY_KEYS.companies,
    queryFn: () => call(listCompanies()),
  });
}

/** How many companies a page of the group's "add a company" search shows. */
export const COMPANY_SEARCH_PAGE_SIZE = 10;

/**
 * One page of the companies a group could take in (#428), searched and paged
 * by the server rather than read whole like `useCompanies`: the registry grows
 * with every customer, and a picker over all of it does not.
 *
 * Newest registration first, because the company being added is most often the
 * one just registered. `withoutGroup` keeps only the companies in no group;
 * off, the companies already in another group come too, for a move.
 *
 * Under `['companies']`, so the membership write that invalidates the
 * companies refetches the page on screen as well.
 */
export function useCompanySearch({
  search,
  page,
  withoutGroup,
}: {
  search: string;
  /** 1-based. */
  page: number;
  withoutGroup: boolean;
}) {
  const q = search.trim();
  return useQuery({
    queryKey: [...QUERY_KEYS.companies, 'search', { q, page, withoutGroup }] as const,
    queryFn: () =>
      call(
        listCompanies({
          query: {
            ...(q !== '' && { q }),
            without_group: withoutGroup,
            sort: CompanyListSort.CREATED_AT,
            direction: SortDirection.DESC,
            limit: COMPANY_SEARCH_PAGE_SIZE,
            offset: (page - 1) * COMPANY_SEARCH_PAGE_SIZE,
          },
        }),
      ),
    // The page on screen stays while the next one loads, so a keystroke or a
    // press of Próxima does not blank the table -- and the search box above it
    // does not lose its place.
    placeholderData: keepPreviousData,
  });
}

/**
 * The text each column of the staff companies list is narrowed by (#439),
 * under the names `GET /companies` reads. A blank or missing one is no filter.
 */
export interface CompanyColumnFilters {
  readonly name?: string;
  readonly slug?: string;
  readonly cnpj?: string;
  readonly group?: string;
}

/** What the staff list asks of `GET /companies` beyond the search and the page. */
export interface CompaniesListing {
  readonly filters?: CompanyColumnFilters;
  /** Absent: the server's own order, by name. */
  readonly sort?: { readonly field: CompanyListSort; readonly direction: SortDirection };
}

/**
 * One page of the registry, for the staff list -- and only for it (#427).
 *
 * `useCompanies()` stays the whole registry: the detail page finds its company
 * inside it and every company select offers all of them, so paging it would
 * quietly drop companies from those screens. This is the other question, "show
 * me the next fifty", ordered by name as the list always was.
 *
 * A blank search sends no `q`, and the first page sends no `offset`, so the
 * plain list is the plainest request. The previous page stays on screen while
 * the next one loads (`keepPreviousData`), so paging never flashes the spinner
 * or an empty state between two full tables.
 *
 * `page` is 1-based, as the address and the pager count it.
 *
 * Since #439 the columns are filtered and sorted by the server too: each
 * non-blank filter goes out under its own parameter, and a sort as `sort` and
 * `direction`. Nothing is sent for what is not set, as with `q`.
 */
export function useCompaniesPage(search: string, page: number, listing: CompaniesListing = {}) {
  const { filters = {}, sort } = listing;
  return useQuery({
    queryKey: QUERY_KEYS.companiesPage(search, page, listing),
    queryFn: () =>
      call(
        listCompanies({
          query: {
            q: unlessBlank(search),
            name: unlessBlank(filters.name ?? ''),
            slug: unlessBlank(filters.slug ?? ''),
            cnpj: unlessBlank(filters.cnpj ?? ''),
            group: unlessBlank(filters.group ?? ''),
            ...(sort !== undefined && { sort: sort.field, direction: sort.direction }),
            limit: PAGE_SIZE.companies,
            ...(page > 1 && { offset: (page - 1) * PAGE_SIZE.companies }),
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * Remember on the server which company the portal should open on next time
 * (#392). Browser storage is off limits (see `session/sessionStore.ts`), so the
 * choice lives on the user record and comes back as the principal's
 * `selected_company`. Nothing on screen waits for it.
 */
export function useRememberSelectedCompany() {
  return useMutation({
    mutationFn: (companyId: string) =>
      call(rememberSelectedCompany({ body: { company_id: companyId } })),
  });
}

/**
 * Remember on the server which columns the signed-in person hid in one table
 * (#444), so the table opens the same way after a reload or on another
 * browser. It comes back as the principal's `hidden_columns`. The table has
 * already hidden the column by the time this goes out, so nothing on screen
 * waits for it, and a failure is logged rather than shown: it costs the person
 * the preference next time, not the change they just made.
 */
export function useRememberTableColumns() {
  return useMutation({
    mutationFn: ({ tableId, hidden }: { tableId: string; hidden: readonly string[] }) =>
      call(rememberTableColumns({ path: { table_id: tableId }, body: { hidden: [...hidden] } })),
    onError: (error) => {
      reportFailure('table-columns', error);
    },
  });
}

/**
 * The contracts the signed-in principal may see -- its own company's, because
 * the backend scopes the query before the handler runs. The upload screen needs
 * one to upload against, and a customer has exactly one today.
 */
export function useContracts() {
  return useQuery({
    queryKey: QUERY_KEYS.contracts,
    queryFn: () => call(listContracts()),
  });
}

// --- Machine credentials ----------------------------------------------------
//
// The three writes share one query key, and each invalidates it, so the list a
// staff member is looking at is the list as it now stands. They are here rather
// than in the screen for the reason the module docstring gives: a key invented
// twice is a cache that stops invalidating.

export function useMachineClients() {
  return useQuery({
    queryKey: QUERY_KEYS.machineClients,
    queryFn: () => call(listMachineClients()),
  });
}

export function useIssueMachineClient() {
  const queryClient = useQueryClient();
  return useMutation<
    MachineClientCreatedResponse,
    unknown,
    MachineClientCreate & { companyId: string }
  >({
    mutationFn: ({ companyId, ...body }) =>
      call(
        issueMachineClient({
          body,
          // The company the credential is for. Sent as the scope header
          // rather than in the body, which is what makes a credential
          // structurally unable to name a company the request is not for.
          headers: { 'X-Company-Id': companyId },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.machineClients }),
  });
}

export function useRotateMachineClient() {
  const queryClient = useQueryClient();
  return useMutation<MachineClientCreatedResponse, unknown, string>({
    mutationFn: (machineClientId) =>
      call(rotateMachineClient({ path: { machine_client_id: machineClientId } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.machineClients }),
  });
}

export function useRevokeMachineClient() {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, string>({
    mutationFn: (machineClientId) =>
      call(revokeMachineClient({ path: { machine_client_id: machineClientId } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.machineClients }),
  });
}

// --- The platform's own A1 certificate ---------------------------------------
//
// Three hooks over one key, each write invalidating it, for the same reason the
// credential hooks above do: installing a certificate changes the status of the
// one it replaced, so a list that was not refetched would show two active
// certificates for one CNPJ.
//
// No company header on any of them. This certificate belongs to the platform
// rather than to a tenant, and the backend takes no scope for it at all.

export function useCertificates() {
  return useQuery({
    queryKey: QUERY_KEYS.certificates,
    queryFn: () => call(listCertificates()),
  });
}

/**
 * Install a certificate, archive and password together.
 *
 * The password travels in the multipart body and is never put anywhere else --
 * not in a query key, not in a cache entry, not in a hook's return value. The
 * response carries metadata only, which is the API's shape rather than this
 * hook's discretion: there is no field on it that could hold either secret.
 */
export function useUploadCertificate() {
  const queryClient = useQueryClient();
  return useMutation<A1CertificateResponse, unknown, { file: File; password: string }>({
    mutationFn: ({ file, password }) => call(uploadCertificate({ body: { file, password } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.certificates }),
  });
}

export function useDeactivateCertificate() {
  const queryClient = useQueryClient();
  return useMutation<A1CertificateResponse, unknown, string>({
    mutationFn: (certificateId) =>
      call(deactivateCertificate({ path: { certificate_id: certificateId } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.certificates }),
  });
}

// --- The connection to eSocial -----------------------------------------------
//
// One hook, and it is a mutation rather than a query even though it reads
// nothing. That is not a technicality: running it opens a real mutual-TLS
// connection to a government service with the platform's own signing
// credential, and a `useQuery` would do that on mount, on window focus and on
// every refetch the cache decided it wanted. This happens when somebody presses
// the button, and at no other moment.
//
// Nothing is cached for the same reason. The answer to "is the connection up?"
// is worth exactly as much as the moment it was taken, and a stale green result
// served out of a cache is worse than no result at all.

export function useEsocialDiagnostics() {
  return useMutation<
    EsocialDiagnosticResponse,
    unknown,
    { companyId: string | null; browserUrl?: string | null }
  >({
    mutationFn: ({ companyId, browserUrl = null }) =>
      call(
        runEsocialDiagnostics({
          // Sent only when somebody typed one: the configured page is the
          // default, and the server is where that default lives.
          body: { company_id: companyId, ...(browserUrl !== null && { browser_url: browserUrl }) },
        }),
      ),
  });
}

// --- The eSocial intake ------------------------------------------------------
//
// The fetch is a mutation for the same reason the diagnostic is, and with one
// more argument behind it: pressing it opens a real connection to a government
// service with the platform's signing credential *and* writes rows. A
// `useQuery` would do that on mount, on window focus and on every refetch the
// cache decided it wanted.
//
// The two reads are ordinary queries, and the fetch invalidates the whole
// `esocial` root: a run creates staged events and a fetch record at once, so a
// listing left on screen after one would be showing a queue that has moved.
//
// **Nothing here reaches the review queue or the roster**, so nothing here
// invalidates them. That is not an oversight -- a fetch cannot change either,
// and a hook that refetched them would suggest it might.

/** What has been staged, narrowed by company and by eSocial event type. */
export function useEsocialEvents(companyId: string | null, eventType: EsocialEventType | null) {
  return useQuery({
    queryKey: QUERY_KEYS.esocialEvents(companyId, eventType),
    queryFn: () =>
      call(
        listEsocialEvents({
          query: {
            ...(companyId !== null && { company_id: companyId }),
            ...(eventType !== null && { event_type: eventType }),
          },
        }),
      ),
  });
}

/** The fetch history: who asked, over which window, and how it went. */
export function useEsocialFetches(companyId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.esocialFetches(companyId),
    queryFn: () =>
      call(listEsocialFetches({ query: companyId === null ? {} : { company_id: companyId } })),
  });
}

export interface FetchEsocialEvents {
  readonly companyId: string;
}

/** Ask eSocial for one company's events now. See the note above. */
export function useFetchEsocialEvents() {
  const queryClient = useQueryClient();
  return useMutation<EsocialFetchResultResponse, unknown, FetchEsocialEvents>({
    mutationFn: ({ companyId }) => call(fetchEsocialEvents({ body: { company_id: companyId } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['esocial'] }),
  });
}

export interface ConfirmEsocialFetch {
  readonly fetchId: string;
  /** The contract to file the movements against. The government does not say which. */
  readonly contractId: string;
}

/**
 * Turn one run's staged events into requested movements.
 *
 * The one hook in this group that invalidates something outside `esocial`, and
 * the reason is the whole of #288: confirming *creates movements*, so the review
 * queue an analyst has open in another tab is now short by however many landed.
 * The fetch above deliberately does not do this, and the contrast is the point
 * -- a fetch cannot change the queue, and a hook that refetched it would suggest
 * it might.
 *
 * Nothing about the roster is invalidated, because nothing about the roster
 * moved: what this creates is `submitted`, and a reviewer decides.
 */
export function useConfirmEsocialFetch() {
  const queryClient = useQueryClient();
  return useMutation<EsocialFetchCommitResponse, unknown, ConfirmEsocialFetch>({
    mutationFn: ({ fetchId, contractId }) =>
      call(
        confirmEsocialFetch({
          path: { fetch_id: fetchId },
          body: { contract_id: contractId },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['esocial'] });
      void queryClient.invalidateQueries({ queryKey: ['review'] });
    },
  });
}

// --- The eSocial portal walk ------------------------------------------------
//
// #451's run endpoint answers when the walk is over -- every stage run, every
// screen sealed -- so starting one is a mutation whose pending state *is* "the
// walk is in progress", and there is nothing to poll: no run is visible to
// anybody until it has finished. The answer is seeded into the run's own key,
// so the stages appear without a second request, and the history is
// invalidated because it has a new first row.
//
// The artefacts are read with `parseAs: 'blob'` on the evidence endpoint. A
// screenshot is a query only so the screen can show it while it is mounted:
// `gcTime: 0` drops the bytes the moment it is not, because the list and
// download screens show real workers. The page HTML, the downloaded report and
// the bundle are mutations -- fetched when somebody asks for them and never
// sitting in a cache because a screen was open. Every read is audited on the
// server, which is one more reason not to refetch any of them on focus.

/** Every walk of one company -- or of every company -- newest first. */
export function useEsocialPortalWalks(companyId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.esocialPortalWalks(companyId),
    queryFn: () =>
      call(listEsocialPortalWalks({ query: companyId === null ? {} : { company_id: companyId } })),
  });
}

/**
 * Which browser this deployment's portal walks drive (#477): headless on the
 * backend, or a remote Chromium with a person at it. Never where it is.
 * `enabled` lets a screen that may not read it skip the request.
 */
export function useEsocialPortalBrowser(enabled = true) {
  return useQuery<EsocialPortalBrowserResponse>({
    queryKey: QUERY_KEYS.esocialPortalBrowser,
    enabled,
    queryFn: () => call(readEsocialPortalBrowser()),
    staleTime: Infinity,
  });
}

/** One walk with its stages. Disabled until there is a run to read. */
export function useEsocialPortalWalk(runId: string | null) {
  return useQuery<EsocialPortalWalkResponse>({
    queryKey: QUERY_KEYS.esocialPortalWalk(runId ?? ''),
    enabled: runId !== null,
    queryFn: () => call(fetchEsocialPortalWalk({ path: { run_id: runId as string } })),
  });
}

/** Walk the portal for one company now. See the note above. */
export function useRunEsocialPortalWalk() {
  const queryClient = useQueryClient();
  return useMutation<EsocialPortalWalkResponse, unknown, EsocialPortalWalkRequest>({
    mutationFn: (body) => call(runEsocialPortalWalk({ body })),
    onSuccess: (run) => {
      queryClient.setQueryData(QUERY_KEYS.esocialPortalWalk(run.id), run);
      void queryClient.invalidateQueries({ queryKey: ['esocial', 'portal-walks', 'list'] });
    },
  });
}

/**
 * Session walks (#479): one login, then every company given. Like a walk, the
 * run endpoint answers when the session is over, so the mutation's pending
 * state is "the session is in progress". Each company's walk is an ordinary
 * walk, so every walk list is invalidated with the session list.
 */
export function useEsocialPortalSessions(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.esocialPortalSessions,
    enabled,
    queryFn: () => call(listEsocialPortalSessions({})),
  });
}

export function useEsocialPortalSession(sessionId: string | null) {
  return useQuery<EsocialPortalSessionResponse>({
    queryKey: QUERY_KEYS.esocialPortalSession(sessionId ?? ''),
    enabled: sessionId !== null,
    queryFn: () => call(fetchEsocialPortalSession({ path: { session_id: sessionId as string } })),
  });
}

export function useRunEsocialPortalSession() {
  const queryClient = useQueryClient();
  return useMutation<EsocialPortalSessionResponse, unknown, EsocialPortalSessionRequest>({
    mutationFn: (body) => call(runEsocialPortalSession({ body })),
    onSuccess: (answer) => {
      queryClient.setQueryData(QUERY_KEYS.esocialPortalSession(answer.id), answer);
      void queryClient.invalidateQueries({ queryKey: ['esocial', 'portal-sessions', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['esocial', 'portal-walks', 'list'] });
    },
  });
}

/** One stage's screenshot, for as long as it is on screen and not a moment longer. */
export function useEsocialPortalWalkScreenshot(runId: string, index: number) {
  return useQuery<Blob>({
    queryKey: QUERY_KEYS.esocialPortalWalkScreenshot(runId, index),
    queryFn: async () =>
      (await call(
        downloadEsocialPortalWalkEvidence({
          path: { run_id: runId, index, artefact: 'screenshot' },
          parseAs: 'blob',
        }),
      )) as Blob,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * The screenshot taken after one action of a stage (#456). Like the stage's
 * own screenshot: fetched while on screen, dropped from the cache the moment
 * it leaves, never refetched -- each read is an audited one.
 */
export function useEsocialPortalWalkActionScreenshot(runId: string, index: number, action: number) {
  return useQuery<Blob>({
    queryKey: QUERY_KEYS.esocialPortalWalkActionScreenshot(runId, index, action),
    queryFn: async () =>
      (await call(
        downloadEsocialPortalWalkActionScreenshot({
          path: { run_id: runId, index, number: action },
          parseAs: 'blob',
        }),
      )) as Blob,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * One stage's trace (#456): its actions, navigations, network log, console
 * and the requests pending when it ended. The contract serves it as bytes --
 * it is an artefact like the others -- so it is read as text and parsed here,
 * into the shape `PortalWalkTrace` names.
 */
export function useEsocialPortalWalkTrace(runId: string, index: number) {
  return useQuery<PortalWalkTrace>({
    queryKey: QUERY_KEYS.esocialPortalWalkTrace(runId, index),
    queryFn: async () => {
      const text = (await call(
        downloadEsocialPortalWalkEvidence({
          path: { run_id: runId, index, artefact: 'trace' },
          parseAs: 'text',
        }),
      )) as unknown as string;
      return JSON.parse(text) as PortalWalkTrace;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export interface PortalWalkArtefactTarget {
  readonly runId: string;
  readonly index: number;
  readonly artefact: 'html' | 'file';
}

/** A stage's page HTML or downloaded report, with the name the server sends it under. */
export function useEsocialPortalWalkArtefact() {
  return useMutation<Attachment, unknown, PortalWalkArtefactTarget>({
    mutationFn: ({ runId, index, artefact }) =>
      callAttachment(
        downloadEsocialPortalWalkEvidence({
          path: { run_id: runId, index, artefact },
          parseAs: 'blob',
        }),
      ),
  });
}

/** The whole walk as one zip, named by the server after the company and the moment. */
export function useEsocialPortalWalkBundle() {
  return useMutation<Attachment, unknown, { readonly runId: string }>({
    mutationFn: ({ runId }) =>
      callAttachment(downloadEsocialPortalWalkBundle({ path: { run_id: runId }, parseAs: 'blob' })),
  });
}

// --- The people who sign in --------------------------------------------------
//
// `GET /api/v1/users` answers within the request's company scope, so narrowing
// the list to one company is the `X-Company-Id` header and nothing else -- the
// same mechanism the credential issue above uses, and the reason the query key
// carries the company: the URL does not.
//
// The hooks take the company as an argument rather than reading it from a
// staff-only context, because the endpoint is not staff-only. A customer
// administrator managing its own people would call these unchanged, with its
// own company, the day that conversation happens.

//
// A user registered at a company group (#391) belongs to no company, so a list
// narrowed by the header never shows one. Narrowing to a group is therefore the
// `group_id` query parameter on the unscoped read instead -- the two filters are
// alternatives, not a combination.

export function useUsers(companyId: string | null, groupId: string | null = null) {
  return useQuery({
    queryKey: QUERY_KEYS.users(companyId, groupId),
    queryFn: () =>
      call(
        listUsers({
          ...(companyId !== null && { headers: { 'X-Company-Id': companyId } }),
          ...(groupId !== null && { query: { group_id: groupId } }),
        }),
      ),
  });
}

export interface CreateUser {
  readonly user: UserCreate;
  /**
   * The company the person belongs to, or `null` for a vyttech role and for a
   * person registered at a company group -- whose group travels in the body as
   * `group_id`, and who must not be sent a company header with it.
   *
   * There is no company field on a user body: the server takes it from the
   * scope header, which is what makes it impossible for a form to create
   * somebody inside a company the request is not for. A vyttech role belongs to
   * no company and the header is left off entirely -- sending one would be
   * asking to create staff inside a tenant, which the server refuses and the
   * database's own check constraint would refuse after it.
   */
  readonly companyId: string | null;
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, unknown, CreateUser>({
    mutationFn: ({ user, companyId }) =>
      call(
        createUser(
          companyId === null
            ? { body: user }
            : { body: user, headers: { 'X-Company-Id': companyId } },
        ),
      ),
    // Every narrowing of the list, not just the one on screen: a new user
    // belongs to exactly one of them and the unnarrowed list shows them all.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export interface DeactivateUser {
  readonly userId: string;
  /**
   * The company of the person being deactivated -- `null` for vyttech staff.
   *
   * Taken from the row rather than from whatever the list is narrowed to. A
   * scoped request cannot see a user outside its company, so sending the
   * screen's filter here would turn deactivating a staff member into a 404.
   */
  readonly companyId: string | null;
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, unknown, DeactivateUser>({
    mutationFn: ({ userId, companyId }) =>
      call(
        deactivateUser({
          path: { user_id: userId },
          ...(companyId !== null && { headers: { 'X-Company-Id': companyId } }),
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

// --- Staff onboarding: companies, insurers, contracts ------------------------
//
// The three writes vyttech staff perform before a customer exists in the
// product at all. Two things about their shape are worth stating here rather
// than in the screen:
//
//  - **A contract names its company through `X-Company-Id`, never through the
//    body.** `POST /contracts` resolves the company from the scope header
//    (`require_company_scope`), so there is no field on a contract body for a
//    company and no way for a form to send one that disagrees with the screen
//    somebody is looking at. Same mechanism as a machine credential above, for
//    the same reason.
//  - **The update is a PATCH with omit-means-unchanged semantics.** The hook
//    takes whatever subset the form decided actually moved and sends only that,
//    so editing the cutoff day cannot silently rewrite the off-cycle rule to
//    whatever the form happened to be holding.

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation<CompanyResponse, unknown, CompanyCreate>({
    mutationFn: (body) => call(createCompany({ body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies }),
  });
}

/**
 * Give a company its CNPJ, or correct the one it has.
 *
 * Its own mutation rather than a general company update because the API models
 * it that way: `POST /companies/{id}/cnpj` is vyttech-only, audited under its
 * own action, and is how a tenant registered before the field existed gets one.
 */
export function useSetCompanyCnpj() {
  const queryClient = useQueryClient();
  return useMutation<CompanyResponse, unknown, { companyId: string; cnpj: string }>({
    mutationFn: ({ companyId, cnpj }) =>
      call(setCompanyCnpj({ path: { company_id: companyId }, body: { cnpj } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies }),
  });
}

/**
 * Set a company's insurer file-name label, or -- with `null` -- clear it.
 *
 * Only the companies: the label is on the company payload, and nothing about
 * the group changes.
 */
export function useSetCompanyFileNameLabel() {
  const queryClient = useQueryClient();
  return useMutation<CompanyResponse, unknown, { companyId: string; label: string | null }>({
    mutationFn: ({ companyId, label }) =>
      call(
        setCompanyFileNameLabel({
          path: { company_id: companyId },
          body: { file_name_label: label },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies }),
  });
}

/**
 * Set what the review does about a company's undocumented terminations.
 *
 * Only the companies: the policy is on the company payload. The review queue
 * reads it on its next fetch, so nothing about the queue is invalidated here.
 */
export function useSetCompanyTerminationDocumentPolicy() {
  const queryClient = useQueryClient();
  return useMutation<
    CompanyResponse,
    unknown,
    { companyId: string; policy: TerminationDocumentPolicy }
  >({
    mutationFn: ({ companyId, policy }) =>
      call(
        setCompanyTerminationDocumentPolicy({
          path: { company_id: companyId },
          body: { termination_document_policy: policy },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies }),
  });
}

// --- Economic groups ----------------------------------------------------------
//
// A registry beside the companies, not a tenant: a group scopes nothing and
// nobody's access changes with it. Membership is set on the company, through
// `PUT /companies/{id}/group`, which is why that one write invalidates both
// roots -- the company's own payload names its group, and the group's payload
// counts and lists its companies. There is no delete, and so no hook for one.

export function useGroups() {
  return useQuery({
    queryKey: QUERY_KEYS.groups,
    queryFn: () => call(listGroups()),
  });
}

/**
 * What one page of the staff groups list asks for: the per-column filters, the
 * sort and the page, as `GET /groups` reads them (#438). `sort: null` is the
 * API's default order, by name. No free-text `q` since the list lost its search
 * box (#447); the add-a-company modal's search of `GET /companies` is its own.
 */
export interface GroupsPageView {
  readonly name: string;
  readonly slug: string;
  readonly sort: { readonly column: CompanyGroupListSort; readonly desc: boolean } | null;
  /** 1-based, as the address and the pager count it. */
  readonly page: number;
}

/**
 * One page of the economic groups, for the staff list (#440).
 *
 * `useGroups()` stays the whole registry, for the reason `useCompanies()` does:
 * the company page's group picker, the staff users page and the new-staff-user
 * form all offer every group, and paging it would quietly drop some.
 *
 * The filtering, the sorting and the paging are the server's; every value left
 * at its default is left out of the request, so the plain list is the plainest
 * request. The previous page stays on screen while the next one loads.
 */
export function useGroupsPage(view: GroupsPageView) {
  return useQuery({
    queryKey: QUERY_KEYS.groupsPage(view),
    queryFn: () =>
      call(
        listGroups({
          query: {
            name: unlessBlank(view.name),
            slug: unlessBlank(view.slug),
            ...(view.sort !== null && { sort: view.sort.column }),
            ...(view.sort?.desc === true && { direction: SortDirection.DESC }),
            limit: PAGE_SIZE.groups,
            ...(view.page > 1 && { offset: (view.page - 1) * PAGE_SIZE.groups }),
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useGroup(groupId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.group(groupId),
    queryFn: () => call(getGroup({ path: { group_id: groupId } })),
  });
}

/**
 * Everyone insured on `asOf` under any contract of any company now in the
 * group (#416), one item per CPF, each entry naming its company.
 *
 * One request for the whole group: the server reads every member's contracts,
 * so the browser never fans out per company or per contract. No `X-Company-Id`
 * -- the read spans the group, and the server refuses a header narrowing it.
 * Not `enabled` for a group with no companies: the answer is known.
 */
export function useGroupRoster(groupId: string, asOf: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.groupRoster(groupId, asOf),
    enabled,
    queryFn: () =>
      call(projectGroupRoster({ path: { group_id: groupId }, query: { as_of: asOf } })),
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation<CompanyGroupDetailResponse, unknown, CompanyGroupCreate>({
    mutationFn: (body) => call(createGroup({ body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.groups }),
  });
}

/** The name only. The slug is stable, and the API has no field for changing it. */
export function useRenameGroup() {
  const queryClient = useQueryClient();
  return useMutation<CompanyGroupDetailResponse, unknown, { groupId: string; name: string }>({
    mutationFn: ({ groupId, name }) =>
      call(renameGroup({ path: { group_id: groupId }, body: { name } })),
    // The companies too: every company payload carries its group's name.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.groups });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies });
    },
  });
}

/**
 * Set a group's insurer file-name label, or -- with `null` -- clear it.
 *
 * The companies too: each member without a label of its own reports the
 * group's as the one in force.
 */
export function useSetGroupFileNameLabel() {
  const queryClient = useQueryClient();
  return useMutation<
    CompanyGroupDetailResponse,
    unknown,
    { groupId: string; label: string | null }
  >({
    mutationFn: ({ groupId, label }) =>
      call(
        setGroupFileNameLabel({ path: { group_id: groupId }, body: { file_name_label: label } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.groups });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies });
    },
  });
}

/**
 * Put a company in a group, move it to another, or -- with `null` -- take it out.
 *
 * A company is in at most one group, so naming a second one is a move: the
 * group it left loses a member as the one it joined gains it. Invalidating the
 * whole `['groups']` root covers both, whichever detail happens to be open.
 */
export function useSetCompanyGroup() {
  const queryClient = useQueryClient();
  return useMutation<CompanyResponse, unknown, { companyId: string; groupId: string | null }>({
    mutationFn: ({ companyId, groupId }) =>
      call(setCompanyGroup({ path: { company_id: companyId }, body: { group_id: groupId } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.groups });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies });
    },
  });
}

/** Every insurer on record, retired ones included -- past contracts point at them. */
export function useInsurers() {
  return useQuery({
    queryKey: QUERY_KEYS.insurers,
    queryFn: () => call(listInsurers()),
  });
}

/** The layouts an insurer may be pointed at -- the only codes the API accepts. */
export function useSnapshotLayouts() {
  return useQuery({
    queryKey: QUERY_KEYS.snapshotLayouts,
    queryFn: () => call(listSnapshotLayouts()),
  });
}

export function useCreateInsurer() {
  const queryClient = useQueryClient();
  return useMutation<InsurerResponse, unknown, InsurerCreate>({
    mutationFn: (body) => call(createInsurer({ body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.insurers }),
  });
}

/**
 * The contracts of one company, read as staff -- which is why the header is
 * here at all.
 *
 * `GET /contracts` is scoped to the principal, and a staff principal's scope is
 * every company. Without the header this would answer with every contract in
 * the product, which on a company's own detail view would be somebody else's
 * data on the wrong screen.
 */
export function useCompanyContracts(companyId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.companyContracts(companyId ?? ''),
    enabled: companyId !== null,
    queryFn: () => call(listContracts({ headers: { 'X-Company-Id': companyId as string } })),
  });
}

// --- One segurado, as staff read them (#418) --------------------------------
//
// The staff person page is addressed by one `insured_person_id` and shows every
// contract of the company the person is under. Nothing that names the person
// travels in a URL, so getting from the one id to the others takes three steps:
//
//  1. **Find the row.** The history endpoint is per contract and the URL does
//     not say which contract the row is on, so it is asked of each of the
//     company's contracts. A company has a handful; all but one answer `404`,
//     which is not retried and is the expected answer, not a failure.
//  2. **Look the person up** with the CPF the found row carries, in the body of
//     `POST /companies/{id}/insured-people/lookup` (#416) -- never in a query
//     string, where a proxy or an access log would keep it.
//  3. **Read each row's history**, under the same key as step 1, so the row
//     already read is not read twice.
//
// Every request carries the page's company as `X-Company-Id`, as
// `useCompanyContracts` does above.

function staffPersonHistoryQuery(companyId: string, contractId: string, personId: string) {
  return {
    queryKey: QUERY_KEYS.staffPersonHistory(companyId, contractId, personId),
    queryFn: () =>
      call(
        getPersonHistory({
          path: { contract_id: contractId, insured_person_id: personId },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  };
}

/** A `404` or `403`: the API's way of saying "nobody you can see", not a failure to load. */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && (error.isNotFound || error.isForbidden);
}

/** What {@link useStaffPersonLookup} reports: an async state, plus the refusal told apart. */
export interface StaffPersonLookup extends AsyncState<InsuredPersonLookupResponse> {
  /** No contract of the company has this row, or the lookup refused it. */
  readonly isNotFound: boolean;
}

/**
 * Who the row in the URL is, across every contract of the company: steps 1 and
 * 2 above.
 *
 * `isNotFound` is set, and `isError` is not, when every contract refused the
 * row -- an invented id, another company's, or a company with no contracts --
 * so the page can say "not here" rather than offer a retry that will never
 * work. Any other failure is a failure, and retrying it retries what failed.
 */
export function useStaffPersonLookup(companyId: string, personId: string): StaffPersonLookup {
  const contracts = useCompanyContracts(companyId);
  const probes = useQueries({
    queries: (contracts.data?.items ?? []).map((contract) =>
      staffPersonHistoryQuery(companyId, contract.id, personId),
    ),
  });
  const found = probes.find((probe) => probe.data !== undefined)?.data;
  const cpf = found?.person.cpf;
  const lookup = useQuery({
    queryKey: QUERY_KEYS.staffPersonLookup(companyId, personId),
    enabled: cpf !== undefined,
    queryFn: () =>
      call(
        lookUpCompanyInsuredPerson({
          path: { company_id: companyId },
          body: { cpf: cpf as string },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });

  const notFound = { isPending: false, isError: false, error: null, data: undefined };
  if (contracts.isPending || contracts.isError) {
    return {
      isPending: contracts.isPending,
      isError: contracts.isError,
      error: contracts.error,
      data: undefined,
      refetch: contracts.refetch,
      isNotFound: false,
    };
  }
  if (found !== undefined) {
    if (isRefusal(lookup.error)) {
      return { ...notFound, isNotFound: true };
    }
    return {
      isPending: lookup.isPending,
      isError: lookup.isError,
      error: lookup.error,
      data: lookup.data,
      refetch: lookup.refetch,
      isNotFound: false,
    };
  }
  if (probes.some((probe) => probe.isPending)) {
    return { ...notFound, isPending: true, isNotFound: false };
  }
  const failed = probes.filter((probe) => !isRefusal(probe.error));
  if (failed.length === 0) {
    return { ...notFound, isNotFound: true };
  }
  return {
    isPending: false,
    isError: true,
    error: failed[0]?.error,
    data: undefined,
    refetch: () => Promise.all(failed.map((probe) => probe.refetch())),
    isNotFound: false,
  };
}

/** Step 3 above: each row's history, in the order of the lookup's entries. */
export function useStaffPersonHistories(
  companyId: string,
  entries: readonly LookupContractEntry[],
): readonly UseQueryResult<PersonHistoryResponse>[] {
  return useQueries({
    queries: entries.map((entry) =>
      staffPersonHistoryQuery(companyId, entry.contract_id, entry.insured_person_id),
    ),
  });
}

export interface CreateContract {
  readonly companyId: string;
  readonly contract: ContractCreate;
}

export function useCreateContract() {
  const queryClient = useQueryClient();
  return useMutation<ContractResponse, unknown, CreateContract>({
    mutationFn: ({ companyId, contract }) =>
      call(createContract({ body: contract, headers: { 'X-Company-Id': companyId } })),
    onSuccess: (_created, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companyContracts(companyId) });
      // The principal-scoped listing every other screen reads is now stale too.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.contracts });
    },
  });
}

export interface UpdateContract {
  readonly companyId: string;
  readonly contractId: string;
  /** Only what moved. An omitted field is left alone by the server. */
  readonly changes: ContractUpdate;
}

export function useUpdateContract() {
  const queryClient = useQueryClient();
  return useMutation<ContractResponse, unknown, UpdateContract>({
    mutationFn: ({ companyId, contractId, changes }) =>
      call(
        updateContract({
          path: { contract_id: contractId },
          body: changes,
          headers: { 'X-Company-Id': companyId },
        }),
      ),
    onSuccess: (_updated, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companyContracts(companyId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.contracts });
    },
  });
}

// --- The baseline population ------------------------------------------------
//
// The one-time import that gives a contract the population it already insures,
// and the log of every time it was run. Three things about the shape are worth
// stating here rather than in the screen:
//
//  - **Every call carries `X-Company-Id`.** These are staff reads and writes
//    against one customer's contract, and a staff principal's own scope is
//    every company there is. The header is what narrows them, exactly as it
//    does for the contract listing above.
//  - **`override` is omitted unless it is true.** The API defaults it to false,
//    so sending `override=false` and sending nothing are the same request --
//    but only one of them is a request that never mentioned the override. A
//    first attempt must be structurally incapable of carrying it.
//  - **The source is parsed as a blob, explicitly.** The endpoint answers with
//    the uploaded bytes under their own media type, and the generated client
//    would otherwise infer `text` from `text/csv` and decode them as UTF-8 --
//    which silently mangles the Latin-1 export this feature exists to accept.

/** Every baseline import run against one contract, newest first. */
export function useBaselineImports(contractId: string | null, companyId: string) {
  return useQuery<BaselineImportListResponse>({
    queryKey: QUERY_KEYS.baselineImports(contractId ?? ''),
    enabled: contractId !== null,
    queryFn: () =>
      call(
        listBaselineImports({
          path: { contract_id: contractId as string },
          headers: companyScope(companyId),
        }),
      ),
  });
}

export interface ImportBaseline {
  readonly contractId: string;
  readonly companyId: string;
  /** The single date the whole file is insured from. */
  readonly baselineDate: string;
  readonly file: File;
  /** Import onto a contract that has already moved on. Never true by default. */
  readonly override?: boolean;
}

export function useImportBaseline() {
  const queryClient = useQueryClient();
  return useMutation<BaselineImportResponse, unknown, ImportBaseline>({
    mutationFn: ({ contractId, companyId, baselineDate, file, override }) =>
      call(
        importBaseline({
          path: { contract_id: contractId },
          body: {
            baseline_date: baselineDate,
            file,
            // Present only when the analyst asked for it. See the note above.
            ...(override === true && { override: true }),
          },
          headers: companyScope(companyId),
        }),
      ),
    onSuccess: (_created, { contractId, companyId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.baselineImports(contractId) });
      // The import writes people, coverage and salaries, so every portal screen
      // reading this company's roster is now showing a population that moved.
      void invalidatePortal(queryClient, companyId);
    },
  });
}

export interface ImportCompanyBaseline {
  readonly companyId: string;
  /** The single date the whole sheet is insured from. */
  readonly baselineDate: string;
  readonly file: File;
  /** Import onto contracts that have already moved on. Never true by default. */
  readonly override?: boolean;
}

/**
 * The customer's sheet, split across the company's contracts by Subestipulante.
 *
 * The company is in the path, so no `X-Company-Id` travels: the server takes
 * the tenant from the address and refuses a header that names another. Every
 * contract the sheet touched has a new import in its history, so the whole
 * family of history queries is invalidated rather than guessing which.
 */
export function useImportCompanyBaseline() {
  const queryClient = useQueryClient();
  return useMutation<CompanyBaselineImportResponse, unknown, ImportCompanyBaseline>({
    mutationFn: ({ companyId, baselineDate, file, override }) =>
      call(
        importCompanyBaseline({
          path: { company_id: companyId },
          body: {
            baseline_date: baselineDate,
            file,
            // Present only when the analyst asked for it, as for one contract.
            ...(override === true && { override: true }),
          },
        }),
      ),
    onSuccess: (_created, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: ['baseline-imports'] });
      void invalidatePortal(queryClient, companyId);
    },
  });
}

export interface ImportGroupBaseline {
  readonly groupId: string;
  /** The companies in the group, whose portal screens the import changes. */
  readonly companyIds: readonly string[];
  /** The single date the whole sheet is insured from. */
  readonly baselineDate: string;
  readonly file: File;
  /** Import onto contracts that have already moved on. Never true by default. */
  readonly override?: boolean;
}

/**
 * The customer's sheet, split across every contract of every company in a
 * group by Subestipulante.
 *
 * No `X-Company-Id` travels, and none may: the import spans the group's
 * companies, and the server refuses a header that would confine it to one.
 */
export function useImportGroupBaseline() {
  const queryClient = useQueryClient();
  return useMutation<GroupBaselineImportResponse, unknown, ImportGroupBaseline>({
    mutationFn: ({ groupId, baselineDate, file, override }) =>
      call(
        importGroupBaseline({
          path: { group_id: groupId },
          body: {
            baseline_date: baselineDate,
            file,
            // Present only when the analyst asked for it, as for one contract.
            ...(override === true && { override: true }),
          },
        }),
      ),
    onSuccess: (_created, { groupId, companyIds }) => {
      void queryClient.invalidateQueries({ queryKey: ['baseline-imports'] });
      // The group's own population read, on the page this import sits on.
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.group(groupId), 'roster'] });
      for (const companyId of companyIds) {
        void invalidatePortal(queryClient, companyId);
      }
    },
  });
}

export interface BaselineImportSource {
  readonly contractId: string;
  readonly companyId: string;
  readonly baselineImportId: string;
}

/**
 * The bytes a baseline was imported from, as they arrived.
 *
 * A mutation rather than a query on purpose: this is CPFs and salaries in the
 * clear, so it is fetched when somebody asks for it and is never sitting in a
 * cache because a screen was open.
 */
export function useBaselineImportSource() {
  return useMutation<Blob, unknown, BaselineImportSource>({
    mutationFn: async ({ contractId, companyId, baselineImportId }) =>
      (await call(
        downloadBaselineImportSource({
          path: { contract_id: contractId, baseline_import_id: baselineImportId },
          headers: companyScope(companyId),
          parseAs: 'blob',
        }),
      )) as Blob,
  });
}

export interface SnapshotFileTarget {
  readonly companyId: string;
  readonly snapshotId: string;
}

/**
 * The rendered file a submission carries, in the insurer's own layout.
 *
 * A mutation rather than a query on the same grounds as the baseline's source
 * bytes: this is the population of a company in the clear, so it is fetched
 * when somebody asks for it and never sits in a cache because a screen was
 * open. It resolves to bytes *and* the name the server stored the file under,
 * so the download is saved as the thing the insurer was sent rather than as
 * something the screen made up out of the fields it had.
 */
export function useSnapshotFile() {
  return useMutation<Attachment, unknown, SnapshotFileTarget>({
    mutationFn: ({ companyId, snapshotId }) =>
      callAttachment(
        downloadSnapshotFile({
          path: { snapshot_id: snapshotId },
          headers: companyScope(companyId),
          parseAs: 'blob',
        }),
      ),
  });
}

export interface NoticeFileTarget {
  readonly companyId: string;
  readonly noticeId: string;
}

/**
 * The composed file of an off-cycle notice, for the same reasons as the
 * snapshot's -- see `useSnapshotFile`.
 */
export function useNoticeFile() {
  return useMutation<Attachment, unknown, NoticeFileTarget>({
    mutationFn: ({ companyId, noticeId }) =>
      callAttachment(
        downloadNoticeFile({
          path: { notice_id: noticeId },
          headers: companyScope(companyId),
          parseAs: 'blob',
        }),
      ),
  });
}

// --- The customer portal ----------------------------------------------------
//
// Every one of these is scoped to a company and, where the resource hangs off a
// contract, to a contract. Both identifiers are part of the query key: two
// companies' rosters are two different answers, and a cache keyed only on
// "roster" would hand the second one the first one's people for as long as the
// data stayed fresh.
//
// The company also travels as `X-Company-Id` on the request itself. See
// `customer/companyScope.tsx` for why the portal sends a header the backend
// calls redundant.

export const PORTAL_KEYS = {
  contracts: (companyId: string) => ['portal', companyId, 'contracts'] as const,
  roster: (companyId: string, contractId: string, asOf: string) =>
    ['portal', companyId, 'roster', contractId, asOf] as const,
  // The staff company page's population (#417). Under the company's portal
  // prefix on purpose: an import or a review decision that invalidates the
  // company's portal reads has changed this answer too.
  companyRoster: (companyId: string, asOf: string) =>
    ['portal', companyId, 'company-roster', asOf] as const,
  personHistory: (companyId: string, contractId: string, personId: string) =>
    ['portal', companyId, 'person-history', contractId, personId] as const,
  deltas: (companyId: string, contractId: string, view: string) =>
    ['portal', companyId, 'deltas', contractId, view] as const,
  documents: (companyId: string) => ['portal', companyId, 'documents'] as const,
  // Keyed by the cutoff as well as the contract: "where does this stand in
  // April?" and "in June?" are different answers, and a cache that conflated
  // them would tell somebody a June movement is due now.
  movementStatus: (companyId: string, contractId: string, cycleMonth: string) =>
    ['portal', companyId, 'movement-status', contractId, cycleMonth] as const,
  notifications: (companyId: string) => ['portal', companyId, 'notifications'] as const,
};

/**
 * How many rows each portal listing reads. Beyond this the API pages.
 *
 * One number per listing rather than one for all of them, because the ceiling
 * is a property of the endpoint and they do not agree: the inbox caps at 100
 * where the movement listings allow 200 or more. Asking for more than an
 * endpoint permits is not a truncated page -- it is a `422` and no rows at all,
 * which renders as a broken panel. A single shared constant hid that difference
 * until it reached a real backend, so the sizes are named for what they page.
 */
const PAGE_SIZE = {
  /** The staff companies list. `GET /companies` allows up to 200. */
  companies: 50,
  /** The staff groups list, the same size as the companies. `GET /groups` allows up to 200. */
  groups: 50,
  deltas: 200,
  documents: 200,
  movementStatus: 200,
  /** `GET /notifications` refuses anything above this. */
  notifications: 100,
} as const;

/** The contracts of one company. A customer has exactly one today. */
export function usePortalContracts(companyId: string) {
  return useQuery({
    queryKey: PORTAL_KEYS.contracts(companyId),
    queryFn: () => call(listContracts({ headers: { 'X-Company-Id': companyId } })),
  });
}

/** Everyone insured under one contract on one date. */
export function useRoster(companyId: string, contractId: string | undefined, asOf: string) {
  return useQuery({
    queryKey: PORTAL_KEYS.roster(companyId, contractId ?? '', asOf),
    enabled: contractId !== undefined,
    queryFn: () =>
      call(
        projectRoster({
          path: { contract_id: contractId as string },
          query: { as_of: asOf },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/**
 * One person's whole history on the contract.
 *
 * A person of another company is a `404` here, not a filtered-out row: the
 * tenancy layer removes them from the query before the handler looks. That is
 * what makes the person route safe to reach by pasted URL.
 */
export function usePersonHistory(
  companyId: string,
  contractId: string | undefined,
  personId: string,
) {
  return useQuery({
    queryKey: PORTAL_KEYS.personHistory(companyId, contractId ?? '', personId),
    enabled: contractId !== undefined,
    queryFn: () =>
      call(
        getPersonHistory({
          path: { contract_id: contractId as string, insured_person_id: personId },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/**
 * One contract's undecided changes, as query options.
 *
 * Shared by the portal's single-contract hook and the staff page's one query
 * per contract, so the two read the same thing under the same cache key.
 */
function pendingDeltasQuery(companyId: string, contractId: string) {
  return {
    queryKey: PORTAL_KEYS.deltas(companyId, contractId, 'pending'),
    queryFn: () =>
      call(
        listDeltas({
          query: {
            contract_id: contractId,
            status: DeltaStatus.SUBMITTED,
            limit: PAGE_SIZE.deltas,
          },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  };
}

/** One contract's approved changes dated from `from` on, as query options. */
function approvedFutureDeltasQuery(companyId: string, contractId: string, from: string) {
  return {
    queryKey: PORTAL_KEYS.deltas(companyId, contractId, `approved-from-${from}`),
    queryFn: () =>
      call(
        listDeltas({
          query: {
            contract_id: contractId,
            status: DeltaStatus.APPROVED,
            effective_from: from,
            limit: PAGE_SIZE.deltas,
          },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  };
}

/** The changes this company has asked for and nobody has decided yet. */
export function usePendingDeltas(companyId: string, contractId: string | undefined) {
  return useQuery({
    ...pendingDeltasQuery(companyId, contractId ?? ''),
    enabled: contractId !== undefined,
  });
}

/**
 * Changes that are settled but have not happened yet.
 *
 * An approved termination effective in two months is already written into the
 * roster, and the roster keeps reporting the person as insured until the date
 * arrives -- so without this the person looks ordinary right up to the day they
 * vanish. `effective_from` is today, and the screen keeps only what falls after
 * today.
 */
export function useApprovedFutureDeltas(
  companyId: string,
  contractId: string | undefined,
  from: string,
) {
  return useQuery({
    ...approvedFutureDeltasQuery(companyId, contractId ?? '', from),
    enabled: contractId !== undefined,
  });
}

/**
 * Everyone insured under any of one company's contracts on one date, one item
 * per person (#416), for the staff company page.
 *
 * The company is in the path and the server needs no header for staff; it is
 * sent anyway, naming the same company, so this read is scoped the way every
 * other read of the company is and a header naming another would be refused.
 */
export function useCompanyRoster(companyId: string, asOf: string) {
  return useQuery({
    queryKey: PORTAL_KEYS.companyRoster(companyId, asOf),
    queryFn: () =>
      call(
        projectCompanyRoster({
          path: { company_id: companyId },
          query: { as_of: asOf },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/** What {@link useCompanyScheduledDeltas} reports: every contract's reads, as one. */
export interface CompanyScheduledDeltas {
  /** Pending and approved-from-`from` changes of every contract, concatenated. */
  readonly deltas: readonly DeltaResponse[];
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * The pending and approved-from-`from` changes of each of a company's
 * contracts, two reads per contract.
 *
 * `GET /deltas` takes one contract at a time, and a company has a handful, so
 * one call each is cheap. They answer one question between them, so a failure
 * of any one is a failure of the whole: a marker list missing one contract's
 * terminations would look complete and be wrong.
 */
export function useCompanyScheduledDeltas(
  companyId: string,
  contractIds: readonly string[],
  from: string,
): CompanyScheduledDeltas {
  return useQueries({
    queries: contractIds.flatMap((contractId) => [
      pendingDeltasQuery(companyId, contractId),
      approvedFutureDeltasQuery(companyId, contractId, from),
    ]),
    combine: combineScheduledDeltas,
  });
}

// At module level so its reference is stable: TanStack Query only memoises a
// `combine` it has seen before, and an inline one would hand the panel a new
// list on every render.
function combineScheduledDeltas(
  results: readonly { data?: DeltaListResponse; isPending: boolean; isError: boolean }[],
): CompanyScheduledDeltas {
  return {
    deltas: results.flatMap((result) => result.data?.items ?? []),
    isPending: results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
  };
}

/** Everything this contract has ever asked for, newest submission first. */
export function useDeltaHistory(companyId: string, contractId: string | undefined) {
  return useQuery({
    queryKey: PORTAL_KEYS.deltas(companyId, contractId ?? '', 'history'),
    enabled: contractId !== undefined,
    queryFn: () =>
      call(
        listDeltas({
          query: { contract_id: contractId as string, limit: PAGE_SIZE.deltas },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/**
 * The company's current paperwork, in one call rather than one per change.
 *
 * Each document names the deltas it is attached to, so the screen indexes this
 * once instead of asking `GET /deltas/{id}/documents` for every row -- which on
 * a month's movements would be a few hundred round trips to render one table.
 */
export function useCompanyDocuments(companyId: string) {
  return useQuery({
    queryKey: PORTAL_KEYS.documents(companyId),
    queryFn: () =>
      call(
        listDocuments({
          query: { current: true, limit: PAGE_SIZE.documents },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/**
 * Where each of this contract's movements stands, relative to one cutoff.
 *
 * `cycleMonth` is optional and normally left out, which asks the API for the
 * current month in São Paulo -- the browser's own clock is not the authority on
 * which cutoff is being worked on, and a laptop set to the wrong month would
 * otherwise quietly change what "agendada" means.
 */
export function useMovementStatus(
  companyId: string,
  contractId: string | undefined,
  cycleMonth?: string,
) {
  return useQuery({
    queryKey: PORTAL_KEYS.movementStatus(companyId, contractId ?? '', cycleMonth ?? 'current'),
    enabled: contractId !== undefined,
    queryFn: () =>
      call(
        fetchMovementStatus({
          path: { contract_id: contractId as string },
          query: {
            limit: PAGE_SIZE.movementStatus,
            ...(cycleMonth === undefined ? {} : { cycle_month: cycleMonth }),
          },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/**
 * What the company has been told, with the number still unread.
 *
 * The badge comes back on the listing rather than from a call of its own, so
 * the number and the list a person is looking at cannot disagree.
 */
export function useNotifications(companyId: string) {
  return useQuery({
    queryKey: PORTAL_KEYS.notifications(companyId),
    queryFn: () =>
      call(
        listNotifications({
          query: { limit: PAGE_SIZE.notifications },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });
}

/**
 * Mark one notice read, and mark them all read.
 *
 * Both invalidate only the notifications key rather than the whole portal
 * subtree: reading a notice changes nothing about the roster, the movements or
 * the paperwork, and refetching all four to grey out one line would be work
 * nobody asked for on every click.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation<NotificationResponse, unknown, { companyId: string; notificationId: string }>({
    mutationFn: ({ companyId, notificationId }) =>
      call(
        markNotificationRead({
          path: { notification_id: notificationId },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
    onSuccess: (_result, variables) =>
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.notifications(variables.companyId) }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation<NotificationsReadResponse, unknown, { companyId: string }>({
    mutationFn: ({ companyId }) =>
      call(markAllNotificationsRead({ headers: { 'X-Company-Id': companyId } })),
    onSuccess: (_result, variables) =>
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.notifications(variables.companyId) }),
  });
}

/**
 * Everything a submission touches, invalidated together.
 *
 * A new change alters the pending list, the scheduled markers on the roster and
 * the history, and an attached document alters the document index. Invalidating
 * the company's whole portal subtree is one line and cannot forget one of them.
 */
function invalidatePortal(queryClient: ReturnType<typeof useQueryClient>, companyId: string) {
  return queryClient.invalidateQueries({ queryKey: ['portal', companyId] });
}

export interface ChangeSubmission {
  readonly companyId: string;
  readonly contractId: string;
  readonly delta: DeltaCreate['delta'];
  /** Signed paperwork to attach, for the actions that can carry it. */
  readonly document?: { readonly file: File; readonly kind: DocumentKind } | undefined;
}

export interface ChangeSubmissionResult {
  readonly delta: DeltaResponse;
  /** True when a change was recorded but its paperwork could not be attached. */
  readonly documentFailed: boolean;
}

/**
 * Ask for one change, and attach its paperwork if there is any.
 *
 * The change goes first. Almost everything that can be wrong with a submission
 * is wrong with the change -- a CPF that does not check out, a date the roster
 * refuses -- and answering that before a file is uploaded means a correction is
 * a correction rather than a second upload of the same PDF.
 *
 * If the upload or the link then fails, the change stands and is reported as
 * such. A recorded change missing its paperwork is a thing the customer can see
 * and fix by cancelling and resubmitting; silently rolling the change back --
 * which the API offers no way to do anyway -- would be worse.
 */
export function useSubmitChange() {
  const queryClient = useQueryClient();
  return useMutation<ChangeSubmissionResult, unknown, ChangeSubmission>({
    mutationFn: async ({ companyId, contractId, delta, document }) => {
      const headers = { 'X-Company-Id': companyId };
      const created = await call(
        submitDelta({ path: { contract_id: contractId }, body: { delta }, headers }),
      );

      if (document === undefined) {
        return { delta: created, documentFailed: false };
      }

      try {
        const stored = await call(
          uploadDocument({ body: { file: document.file, kind: document.kind }, headers }),
        );
        await call(
          linkDocument({
            path: { document_id: stored.id },
            body: { delta_id: created.id },
            headers,
          }),
        );
        return { delta: created, documentFailed: false };
      } catch {
        return { delta: created, documentFailed: true };
      }
    },
    onSuccess: (_result, variables) => invalidatePortal(queryClient, variables.companyId),
  });
}

/**
 * Withdraw a change nobody has decided yet.
 *
 * There is no edit anywhere in this portal, and that is the API's shape rather
 * than a simplification: a delta is cancelled and resubmitted, and the
 * withdrawn request stays visible in the history. See #210.
 */
export function useCancelChange() {
  const queryClient = useQueryClient();
  return useMutation<DeltaResponse, unknown, { companyId: string; deltaId: string }>({
    mutationFn: ({ companyId, deltaId }) =>
      call(
        cancelDelta({
          path: { delta_id: deltaId },
          body: {},
          headers: { 'X-Company-Id': companyId },
        }),
      ),
    onSuccess: (_result, variables) => invalidatePortal(queryClient, variables.companyId),
  });
}

// --- The vyttech review queue -----------------------------------------------
//
// Reads take no company header: vyttech staff read across every company by
// default, which is what the front page of the back-office is. Writes take one,
// because a write always names the tenant it is for -- so every mutation here
// carries the contract's own `company_id`, read off the listing that produced
// it rather than chosen anywhere in the UI.
//
// Every decision invalidates the whole `['review']` subtree. Deciding one
// movement changes the counts on the contract line, what is in the cycle, and
// what the preview would produce; invalidating only the list it came from would
// leave two of those three stale on screen. Invalidating the preview only
// refetches the draft built *before* the decision, though: rebuilding it is the
// review page's job, because only the page knows whether one was ever asked for.

/** The company a write acts inside, as the scope header the API expects. */
function companyScope(companyId: string): Record<string, string> {
  return { 'X-Company-Id': companyId };
}

export function useReviewQueue(cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.reviewQueue(cycleMonth),
    queryFn: () => call(fetchReviewQueue({ query: { cycle_month: cycleMonth } })),
  });
}

export function useReviewCycle(contractId: string | null, cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.reviewCycle(contractId ?? '', cycleMonth),
    enabled: contractId !== null,
    queryFn: () =>
      call(
        fetchReviewCycle({
          path: { contract_id: contractId as string },
          query: { cycle_month: cycleMonth },
        }),
      ),
  });
}

export function useUpcomingMovements(contractId: string | null, cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.reviewUpcoming(contractId ?? '', cycleMonth),
    enabled: contractId !== null,
    queryFn: () =>
      call(
        fetchUpcomingMovements({
          path: { contract_id: contractId as string },
          query: { cycle_month: cycleMonth },
        }),
      ),
  });
}

export function useLateArrivals(contractId: string | null, cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.reviewLateArrivals(contractId ?? '', cycleMonth),
    enabled: contractId !== null,
    queryFn: () =>
      call(
        fetchLateArrivals({
          path: { contract_id: contractId as string },
          query: { cycle_month: cycleMonth },
        }),
      ),
  });
}

/**
 * The file already built for this cutoff, if one has been.
 *
 * A 404 means nobody has previewed it yet, which is the ordinary state of an
 * open cycle rather than a failure -- so it is not retried and the screen reads
 * `isError` as "no preview yet".
 */
export function useCyclePreview(contractId: string | null, cycleMonth: string) {
  return useQuery<CyclePreviewResponse>({
    queryKey: QUERY_KEYS.reviewPreview(contractId ?? '', cycleMonth),
    enabled: contractId !== null,
    retry: false,
    queryFn: () =>
      call(
        fetchCyclePreview({
          path: { contract_id: contractId as string },
          query: { cycle_month: cycleMonth },
        }),
      ),
  });
}

/** Which snapshot to read, as of which contents, and inside which tenant. */
export interface SnapshotTarget {
  readonly companyId: string;
  readonly snapshotId: string;
  readonly sha256: string;
}

/**
 * One snapshot as the file it renders: the layout's columns, every row and the
 * people held back.
 *
 * Keyed on the digest as well as the id -- see `QUERY_KEYS.snapshot` -- so a
 * draft rebuilt in place is read again rather than served from the cache. The
 * request names the company because a staff principal has no company of its
 * own, and the server answers another company's snapshot with a 404.
 */
export function useSnapshot(target: SnapshotTarget | null) {
  return useQuery<SnapshotDetailResponse>({
    queryKey: QUERY_KEYS.snapshot(target?.snapshotId ?? '', target?.sha256 ?? ''),
    enabled: target !== null,
    queryFn: () =>
      call(
        fetchSnapshot({
          path: { snapshot_id: (target as SnapshotTarget).snapshotId },
          headers: companyScope((target as SnapshotTarget).companyId),
        }),
      ),
  });
}

/** Everything a decision on one movement needs: which one, and inside which tenant. */
export interface ReviewDecision {
  readonly deltaId: string;
  readonly companyId: string;
  readonly reason?: string;
  readonly effectiveDate?: string;
}

// Every decision on a movement carries this key, so the cycle gate can tell a
// decision is still in flight -- the digest on screen is about to stop being
// the cycle's -- without the listing that made it having to say so.
const REVIEW_DECISION_KEY = ['review-decision'] as const;

/** How many decisions on movements are still waiting on the server. */
export function useReviewDecisionsInFlight(): number {
  return useIsMutating({ mutationKey: REVIEW_DECISION_KEY });
}

function useReviewMutation<TResult>(run: (decision: ReviewDecision) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, unknown, ReviewDecision>({
    mutationKey: REVIEW_DECISION_KEY,
    mutationFn: run,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['review'] }),
  });
}

export function useApproveReviewItem() {
  return useReviewMutation<DeltaResponse>(({ deltaId, companyId }) =>
    call(
      approveReviewItem({
        path: { delta_id: deltaId },
        headers: companyScope(companyId),
      }),
    ),
  );
}

export function useRejectReviewItem() {
  return useReviewMutation<DeltaResponse>(({ deltaId, companyId, reason }) =>
    call(
      rejectReviewItem({
        path: { delta_id: deltaId },
        body: { reason: reason ?? '' },
        headers: companyScope(companyId),
      }),
    ),
  );
}

export function useReturnReviewItem() {
  return useReviewMutation<DeltaResponse>(({ deltaId, companyId, reason }) =>
    call(
      returnReviewItem({
        path: { delta_id: deltaId },
        body: { reason: reason ?? '' },
        headers: companyScope(companyId),
      }),
    ),
  );
}

export function useCorrectEffectiveDate() {
  return useReviewMutation<DeltaResponse>(({ deltaId, companyId, effectiveDate }) =>
    call(
      correctReviewItemDate({
        path: { delta_id: deltaId },
        body: { effective_date: effectiveDate ?? '' },
        headers: companyScope(companyId),
      }),
    ),
  );
}

/** What every cycle-level write needs: which contract, which cycle, which tenant. */
export interface CycleTarget {
  readonly contractId: string;
  readonly companyId: string;
  readonly cycleMonth: string;
}

export function useBuildCyclePreview() {
  const queryClient = useQueryClient();
  return useMutation<CyclePreviewResponse, unknown, CycleTarget>({
    mutationFn: ({ contractId, companyId, cycleMonth }) =>
      call(
        buildCyclePreview({
          path: { contract_id: contractId },
          query: { cycle_month: cycleMonth },
          headers: companyScope(companyId),
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['review'] }),
  });
}

/**
 * Sign the cutoff off, over exactly the file that was previewed.
 *
 * Both the identifier and the digest are sent. Either one that does not match
 * what the server holds is a 409, which is the whole point: if the preview was
 * regenerated between the analyst reading it and pressing the button, they would
 * otherwise be approving a file nobody read.
 */
export interface CycleApprovalTarget extends CycleTarget {
  readonly snapshotId: string;
  readonly snapshotSha256: string;
  readonly acknowledgeExceptions?: boolean;
}

export function useApproveReviewCycle() {
  const queryClient = useQueryClient();
  return useMutation<CycleApprovalResponse, unknown, CycleApprovalTarget>({
    mutationFn: ({
      contractId,
      companyId,
      cycleMonth,
      snapshotId,
      snapshotSha256,
      acknowledgeExceptions = false,
    }) =>
      call(
        approveReviewCycle({
          path: { contract_id: contractId },
          body: {
            cycle_month: cycleMonth,
            snapshot_id: snapshotId,
            snapshot_sha256: snapshotSha256,
            acknowledge_exceptions: acknowledgeExceptions,
          },
          headers: companyScope(companyId),
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['review'] }),
  });
}

export interface CycleReopenTarget extends CycleTarget {
  readonly reason: string;
}

export function useReopenReviewCycle() {
  const queryClient = useQueryClient();
  return useMutation<CycleApprovalResponse, unknown, CycleReopenTarget>({
    mutationFn: ({ contractId, companyId, cycleMonth, reason }) =>
      call(
        reopenReviewCycle({
          path: { contract_id: contractId },
          body: { cycle_month: cycleMonth, reason },
          headers: companyScope(companyId),
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['review'] }),
  });
}

// --- The monthly submission -------------------------------------------------
//
// The last stretch of the cycle: package the approved file, file it, mark it
// sent. Reads take no company header for the same reason the review queue's do
// not -- the calendar is the front page of the back-office and spans every
// company in scope -- and every write carries the contract's own `company_id`,
// read off the listing that produced it.
//
// A write invalidates both roots. Marking March sent changes the rhythm *and*
// the review queue underneath it: the watermark has moved, so a movement dated
// inside March is a late arrival from that moment on, and a queue left on
// screen would still be offering decisions on it.

/** The month across every contract in scope, as the calendar reads it. */
export function useSubmissionCalendar(cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.submissionCalendar(cycleMonth),
    queryFn: () => call(fetchSubmissionCalendar({ query: { cycle_month: cycleMonth } })),
  });
}

export function useSubmissionStanding(contractId: string | null, cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.submissionStanding(contractId ?? '', cycleMonth),
    enabled: contractId !== null,
    queryFn: () =>
      call(
        fetchSubmissionStanding({
          path: { contract_id: contractId as string },
          query: { cycle_month: cycleMonth },
        }),
      ),
  });
}

/**
 * Every attempt ever made on one contract, refused ones included.
 *
 * Deliberately not filtered by cycle. "March was refused on the 3rd and filed on
 * the 4th" is the thing this listing exists to say, and an analyst looking at
 * April still wants to see that March went.
 */
export function useSubmissionHistory(contractId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.submissionHistory(contractId ?? ''),
    enabled: contractId !== null,
    queryFn: () => call(fetchSubmissionHistory({ path: { contract_id: contractId as string } })),
  });
}

/** Invalidate the rhythm and the queue under it. See the note above. */
function useSubmissionMutation<TVariables>(
  run: (variables: TVariables) => Promise<SubmissionResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation<SubmissionResponse, unknown, TVariables>({
    mutationFn: run,
    onSuccess: (submission) => {
      const cycleMonth = submission.cycle_month;
      // No cycle month: a manual watermark on the history table -- it does not
      // sit on the calendar and the detail page does not show it. Skip the
      // standing patch.
      if (cycleMonth != null) {
        const standingKey = QUERY_KEYS.submissionStanding(submission.contract_id, cycleMonth);
        const calendarKey = QUERY_KEYS.submissionCalendar(cycleMonth);

        // Patch the standing for this contract from the mutation's own
        // response. The panel and the calendar line render from
        // `CycleStandingResponse`, and what the server would next report is a
        // deterministic function of the returned submission -- so the cache
        // can be brought up to date from data already in hand, instead of
        // waiting for a refetch the server cannot always answer.
        queryClient.setQueryData<CycleStandingResponse | undefined>(standingKey, (current) => {
          if (current === undefined) {
            return current;
          }
          return applySubmissionToStanding(current, submission);
        });

        // The calendar lists every contract's standing for the month; patch
        // the matching item in place so its badge and overdue mark move
        // together with the detail page.
        queryClient.setQueryData<SubmissionCalendarResponse | undefined>(calendarKey, (current) => {
          if (current === undefined) {
            return current;
          }
          const items = current.items.map((line) =>
            line.contract_id === submission.contract_id
              ? applySubmissionToStanding(line, submission)
              : line,
          );
          return {
            ...current,
            items,
            // The rhythm's counts are read from the same items. Recomputing
            // them here means the badges the calendar owes its name to stay
            // honest on the same render that shows the updated line.
            outstanding_count: items.filter((line) => line.stage !== CycleStage.SENT).length,
            overdue_count: items.filter((line) => line.is_overdue).length,
          };
        });
      }

      // Refetch the rhythm's listing and history -- but *not* the standing
      // for this contract, which the patch above has already brought up to
      // date. Refetching it would be a round trip to be told what this
      // response already said, and the window in which it answers with
      // something staler is a window in which the screen goes backwards.
      void queryClient.invalidateQueries({
        queryKey: ['submissions'],
        predicate: (query) => query.queryKey[1] !== 'standing',
      });
      void queryClient.invalidateQueries({ queryKey: ['review'] });
    },
  });
}

/**
 * Package the approved file, naming the digest the approval reported.
 *
 * `snapshotSha256` is sent rather than omitted even though the API accepts it
 * either way: it is the check that the bytes about to leave the building are
 * the ones the sign-off pinned, and a client that has just read the approval
 * has the value in hand. Leaving it out would trade a `409` for a file filed by
 * mistake.
 */
export interface PrepareSubmissionTarget extends CycleTarget {
  readonly snapshotSha256?: string;
}

export function usePrepareSubmission() {
  return useSubmissionMutation<PrepareSubmissionTarget>(
    ({ contractId, companyId, cycleMonth, snapshotSha256 }) =>
      call(
        prepareSubmission({
          path: { contract_id: contractId },
          body: { cycle_month: cycleMonth, snapshot_sha256: snapshotSha256 ?? null },
          headers: companyScope(companyId),
        }),
      ),
  );
}

/**
 * The marker, and the proof behind it.
 *
 * At least one of `reference`, `proofDocumentId` and `notes` has to carry
 * something or the server refuses with a `422`. The screen says so before the
 * request is made, but the rule is the server's: a month marked as sent with
 * nothing behind it evidences nothing, and that has to be true of every client
 * rather than of this one.
 */
export interface MarkSentTarget {
  readonly submissionId: string;
  readonly companyId: string;
  readonly channel: SubmissionChannel;
  readonly reference?: string;
  readonly proofDocumentId?: string;
  readonly notes?: string;
  readonly sentAt?: string;
}

export function useMarkSubmissionSent() {
  return useSubmissionMutation<MarkSentTarget>(
    ({ submissionId, companyId, channel, reference, proofDocumentId, notes, sentAt }) =>
      call(
        markSubmissionSent({
          path: { submission_id: submissionId },
          body: {
            channel,
            reference: reference ?? null,
            proof_document_id: proofDocumentId ?? null,
            notes: notes ?? null,
            sent_at: sentAt ?? null,
          },
          headers: companyScope(companyId),
        }),
      ),
  );
}

export interface MarkFailedTarget {
  readonly submissionId: string;
  readonly companyId: string;
  readonly reason: string;
}

export function useMarkSubmissionFailed() {
  return useSubmissionMutation<MarkFailedTarget>(({ submissionId, companyId, reason }) =>
    call(
      markSubmissionFailed({
        path: { submission_id: submissionId },
        body: { reason },
        headers: companyScope(companyId),
      }),
    ),
  );
}

// --- One file across a company's contracts ----------------------------------
//
// When an insurer's layout takes one file per company and month, the company's
// contracts are filed together. A write here marks every member contract too,
// so it invalidates both roots wholesale: every member's standing, the
// calendar lines and the review queue underneath them have all moved.

/** One company's month with one insurer, member by member. */
export function useConsolidatedStanding(companyId: string, insurerId: string, cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.consolidatedStanding(companyId, insurerId, cycleMonth),
    queryFn: () =>
      call(
        fetchConsolidatedStanding({
          path: { company_id: companyId, insurer_id: insurerId },
          query: { cycle_month: cycleMonth },
          headers: companyScope(companyId),
        }),
      ),
  });
}

function useConsolidatedMutation<TVariables>(
  run: (variables: TVariables) => Promise<ConsolidatedSubmissionResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation<ConsolidatedSubmissionResponse, unknown, TVariables>({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['submissions'] });
      void queryClient.invalidateQueries({ queryKey: ['review'] });
    },
  });
}

export interface ConsolidatedTarget {
  readonly companyId: string;
  readonly insurerId: string;
  readonly cycleMonth: string;
}

/** Package the company's month as one file, every member with it. */
export function usePrepareConsolidated() {
  return useConsolidatedMutation<ConsolidatedTarget>(({ companyId, insurerId, cycleMonth }) =>
    call(
      prepareConsolidated({
        path: { company_id: companyId, insurer_id: insurerId },
        body: { cycle_month: cycleMonth },
        headers: companyScope(companyId),
      }),
    ),
  );
}

export interface ConsolidatedMarkSentTarget extends Omit<MarkSentTarget, 'submissionId'> {
  readonly consolidatedId: string;
}

/** The marker on the company's file, which marks every member the same way. */
export function useMarkConsolidatedSent() {
  return useConsolidatedMutation<ConsolidatedMarkSentTarget>(
    ({ consolidatedId, companyId, channel, reference, proofDocumentId, notes, sentAt }) =>
      call(
        markConsolidatedSent({
          path: { consolidated_id: consolidatedId },
          body: {
            channel,
            reference: reference ?? null,
            proof_document_id: proofDocumentId ?? null,
            notes: notes ?? null,
            sent_at: sentAt ?? null,
          },
          headers: companyScope(companyId),
        }),
      ),
  );
}

export interface ConsolidatedMarkFailedTarget extends Omit<MarkFailedTarget, 'submissionId'> {
  readonly consolidatedId: string;
}

export function useMarkConsolidatedFailed() {
  return useConsolidatedMutation<ConsolidatedMarkFailedTarget>(
    ({ consolidatedId, companyId, reason }) =>
      call(
        markConsolidatedFailed({
          path: { consolidated_id: consolidatedId },
          body: { reason },
          headers: companyScope(companyId),
        }),
      ),
  );
}

export interface ConsolidatedFileTarget {
  readonly companyId: string;
  readonly consolidatedId: string;
}

/** The company's file, fetched when asked for and never cached -- see `useSnapshotFile`. */
export function useConsolidatedFile() {
  return useMutation<Attachment, unknown, ConsolidatedFileTarget>({
    mutationFn: ({ companyId, consolidatedId }) =>
      callAttachment(
        downloadConsolidatedFile({
          path: { consolidated_id: consolidatedId },
          headers: companyScope(companyId),
          parseAs: 'blob',
        }),
      ),
  });
}

// --- One file across an economic group's companies --------------------------
//
// The company file one level up. A group file spans companies, so these calls
// carry no `X-Company-Id`: staff read and work it across every company, and the
// server refuses a request scoped to one. A write marks every member contract
// of every company in the file, so it invalidates both roots wholesale.

/** One group's month with one insurer, member by member. */
export function useGroupFileStanding(groupId: string, insurerId: string, cycleMonth: string) {
  return useQuery({
    queryKey: QUERY_KEYS.groupFileStanding(groupId, insurerId, cycleMonth),
    queryFn: () =>
      call(
        fetchGroupFileStanding({
          path: { group_id: groupId, insurer_id: insurerId },
          query: { cycle_month: cycleMonth },
        }),
      ),
  });
}

function useGroupFileMutation<TVariables>(
  run: (variables: TVariables) => Promise<GroupSubmissionResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation<GroupSubmissionResponse, unknown, TVariables>({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['submissions'] });
      void queryClient.invalidateQueries({ queryKey: ['review'] });
    },
  });
}

export interface GroupFileTarget {
  readonly groupId: string;
  readonly insurerId: string;
  readonly cycleMonth: string;
}

/** Package the group's month as one file, every member with it. */
export function usePrepareGroupFile() {
  return useGroupFileMutation<GroupFileTarget>(({ groupId, insurerId, cycleMonth }) =>
    call(
      prepareGroupFile({
        path: { group_id: groupId, insurer_id: insurerId },
        body: { cycle_month: cycleMonth },
      }),
    ),
  );
}

export interface GroupFileMarkSentTarget {
  readonly groupFileId: string;
  readonly channel: SubmissionChannel;
  readonly reference?: string;
  readonly notes?: string;
  readonly sentAt?: string;
}

/** The marker on the group's file, which marks every member the same way. */
export function useMarkGroupFileSent() {
  return useGroupFileMutation<GroupFileMarkSentTarget>(
    ({ groupFileId, channel, reference, notes, sentAt }) =>
      call(
        markGroupFileSent({
          path: { group_file_id: groupFileId },
          body: {
            channel,
            reference: reference ?? null,
            notes: notes ?? null,
            sent_at: sentAt ?? null,
          },
        }),
      ),
  );
}

export interface GroupFileMarkFailedTarget {
  readonly groupFileId: string;
  readonly reason: string;
}

export function useMarkGroupFileFailed() {
  return useGroupFileMutation<GroupFileMarkFailedTarget>(({ groupFileId, reason }) =>
    call(
      markGroupFileFailed({
        path: { group_file_id: groupFileId },
        body: { reason },
      }),
    ),
  );
}

/** The group's file, fetched when asked for and never cached -- see `useSnapshotFile`. */
export function useGroupFileDownload() {
  return useMutation<Attachment, unknown, { readonly groupFileId: string }>({
    mutationFn: ({ groupFileId }) =>
      callAttachment(
        downloadGroupFile({
          path: { group_file_id: groupFileId },
          parseAs: 'blob',
        }),
      ),
  });
}

// --- The off-cycle notice ----------------------------------------------------
//
// The exception route beside the monthly rhythm. Its own query root rather than
// a branch of `['submissions']`, because raising a notice changes nothing about
// any month's stage: the movement it names is still on the next file, and the
// watermark has not moved. Invalidating the rhythm from here would refetch a
// page of contracts that cannot have changed.
//
// A write does invalidate `['off-cycle']` wholesale, which is right: telling
// the insurer about a movement takes it out of the eligible listing and puts it
// on a notice, and those are the two things on screen.

/** Approved movements this contract's insurer has not been told about. */
export function useEligibleMovements(contractId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.offCycleEligible(contractId ?? ''),
    enabled: contractId !== null,
    queryFn: () => call(fetchEligibleMovements({ path: { contract_id: contractId as string } })),
  });
}

/**
 * Every notice raised on one contract, refused ones included.
 *
 * Refused ones are the point, as they are in the monthly history: "they would
 * not take it off-cycle" is the evidence that this contract's `off_cycle_rule`
 * was wrong, and a listing that showed only what worked would lose it.
 */
export function useOffCycleNotices(contractId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.offCycleNotices(contractId ?? ''),
    enabled: contractId !== null,
    queryFn: () => call(listOffCycleNotices({ query: { contract_id: contractId as string } })),
  });
}

function useOffCycleMutation<TVariables>(run: (variables: TVariables) => Promise<NoticeResponse>) {
  const queryClient = useQueryClient();
  return useMutation<NoticeResponse, unknown, TVariables>({
    mutationFn: run,
    onSuccess: (notice) => {
      // Patch the notices listing from the mutation's own response, on the
      // same grounds as the monthly rhythm's standing: the notice the server
      // just answered with is the listing's newest row, so the panel can show
      // it on this render rather than on the one a refetch eventually brings
      // -- which, before this, was the one an analyst got by pressing F5.
      const noticesKey = QUERY_KEYS.offCycleNotices(notice.contract_id);
      queryClient.setQueryData<NoticeListResponse | undefined>(noticesKey, (current) => {
        if (current === undefined) {
          return current;
        }
        // The listing is newest raised first, and a notice the server has
        // not reported yet (raise) is by construction the newest. A marked
        // one replaces the row it already has.
        const known = current.items.some((row) => row.id === notice.id);
        const items = known
          ? current.items.map((row) => (row.id === notice.id ? notice : row))
          : [notice, ...current.items];
        return { ...current, items, count: items.length };
      });

      // A raised notice takes its movements out of the waiting listing; a
      // refusal puts them back, but what returns -- cutoffs, day counts --
      // is the server's to recompute. Only the first can be patched from
      // what this response carries; the second still needs a refetch.
      if (notice.status === OffCycleNoticeStatus.PREPARED) {
        const eligibleKey = QUERY_KEYS.offCycleEligible(notice.contract_id);
        const carried = new Set(notice.items.map((item) => item.delta_id));
        queryClient.setQueryData<EligibleMovementListResponse | undefined>(
          eligibleKey,
          (current) => {
            if (current === undefined) {
              return current;
            }
            const items = current.items.filter((row) => !carried.has(row.delta.id));
            return {
              ...current,
              items,
              count: items.length,
              notifiable_count: items.filter((row) => row.is_notifiable).length,
            };
          },
        );
      }

      // Refetch what the response cannot recompute -- the waiting listing
      // after a refusal, above all, whose cutoffs and day counts are the
      // server's arithmetic and not ours. Not the listings just patched,
      // though: those would be asking to be told what we already know.
      // The key is matched structurally rather than by identity:
      // `Set.has` on arrays would compare references, and the query's own
      // key is a different array instance from the one built here.
      const noticeContractId = notice.contract_id;
      const eligiblePatched = notice.status === OffCycleNoticeStatus.PREPARED;
      void queryClient.invalidateQueries({
        queryKey: ['off-cycle'],
        predicate: (query) => {
          const [root, kind, id] = query.queryKey as readonly unknown[];
          if (root !== 'off-cycle' || id !== noticeContractId) {
            return true;
          }
          if (kind === 'notices') {
            return false;
          }
          return !(eligiblePatched && kind === 'eligible');
        },
      });
    },
  });
}

export interface RaiseNoticeTarget {
  readonly contractId: string;
  readonly companyId: string;
  readonly deltaIds: readonly string[];
  readonly reason: string;
}

export function useRaiseOffCycleNotice() {
  return useOffCycleMutation<RaiseNoticeTarget>(({ contractId, companyId, deltaIds, reason }) =>
    call(
      raiseOffCycleNotice({
        path: { contract_id: contractId },
        body: { delta_ids: [...deltaIds], reason },
        headers: companyScope(companyId),
      }),
    ),
  );
}

/**
 * The marker, and the proof behind it.
 *
 * The same three forms the monthly one takes, and the server enforces the same
 * "at least one" rule. It binds harder here: an off-cycle notice leaves no
 * monthly file behind it, so this row is the only record that it happened.
 */
export interface MarkNoticeSentTarget {
  readonly noticeId: string;
  readonly companyId: string;
  readonly channel: SubmissionChannel;
  readonly reference?: string;
  readonly notes?: string;
}

export function useMarkNoticeSent() {
  return useOffCycleMutation<MarkNoticeSentTarget>(
    ({ noticeId, companyId, channel, reference, notes }) =>
      call(
        markNoticeSent({
          path: { notice_id: noticeId },
          body: {
            channel,
            reference: reference ?? null,
            proof_document_id: null,
            notes: notes ?? null,
            sent_at: null,
          },
          headers: companyScope(companyId),
        }),
      ),
  );
}

export interface MarkNoticeFailedTarget {
  readonly noticeId: string;
  readonly companyId: string;
  readonly reason: string;
}

export function useMarkNoticeFailed() {
  return useOffCycleMutation<MarkNoticeFailedTarget>(({ noticeId, companyId, reason }) =>
    call(
      markNoticeFailed({
        path: { notice_id: noticeId },
        body: { reason },
        headers: companyScope(companyId),
      }),
    ),
  );
}

// --- The mail bridge --------------------------------------------------------
//
// A temporary path, and the screen over it exists to be watched rather than
// worked in: what it answers is "how much is still coming in by e-mail, and how
// much of that needs somebody?". None of these reads takes a company header,
// because the mailbox is not a tenant's -- a message from an address nobody has
// registered belongs to no company at all, which is exactly the one somebody
// has to act on.
//
// Every write invalidates the whole `mailbox` root. Polling creates messages,
// retrying moves one out of the queue and registering an address changes what
// the *next* retry will do, so a listing left on screen after any of the three
// would be offering actions on a queue that has moved.

/** The log, or just what is still waiting for somebody. */
export function useMailMessages(outstandingOnly: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.mailMessages(outstandingOnly),
    queryFn: () => call(listMailMessages({ query: outstandingOnly ? { outstanding: true } : {} })),
  });
}

/**
 * One message and what became of each attachment.
 *
 * `attempt` is how the earlier pass is read back -- what we made of a message
 * before somebody registered its sender is kept rather than overwritten, and a
 * screen that could not show it would be hiding half of what the bridge did.
 */
export function useMailMessage(messageId: string | null, attempt: number | null = null) {
  return useQuery({
    queryKey: QUERY_KEYS.mailMessage(messageId ?? '', attempt),
    enabled: messageId !== null,
    queryFn: () =>
      call(
        getMailMessage({
          path: { message_id: messageId as string },
          query: attempt === null ? {} : { attempt },
        }),
      ),
  });
}

/** The addresses vyttech has agreed to trust, live ones first. */
export function useMailboxSenders() {
  return useQuery({
    queryKey: QUERY_KEYS.mailboxSenders,
    queryFn: () => call(listMailboxSenders({})),
  });
}

/** Everything the bridge shows, invalidated together. See the note above. */
function useMailboxMutation<TResult, TVariables>(run: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, unknown, TVariables>({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      // A poll or a retry creates spreadsheet previews and supporting documents,
      // which the portal is showing somewhere else in the same session.
      void queryClient.invalidateQueries({ queryKey: ['portal'] });
    },
  });
}

export function usePollMailbox() {
  return useMailboxMutation<MailboxPollResponse, void>(() => call(pollMailbox({})));
}

export function useRetryMailMessage() {
  return useMailboxMutation<MailMessageDetailResponse, string>((messageId) =>
    call(retryMailMessage({ path: { message_id: messageId } })),
  );
}

export interface DiscardMailMessage {
  readonly messageId: string;
  readonly reason: string;
}

export function useDiscardMailMessage() {
  return useMailboxMutation<MailMessageResponse, DiscardMailMessage>(({ messageId, reason }) =>
    call(discardMailMessage({ path: { message_id: messageId }, body: { reason } })),
  );
}

export interface RegisterMailboxSender {
  readonly contractId: string;
  readonly address: string;
  readonly label?: string;
  readonly defaultDocumentKind?: MailboxDocumentKind;
}

export function useRegisterMailboxSender() {
  return useMailboxMutation<MailboxSenderResponse, RegisterMailboxSender>(
    ({ contractId, address, label, defaultDocumentKind }) =>
      call(
        registerMailboxSender({
          body: {
            contract_id: contractId,
            address,
            label: label ?? null,
            ...(defaultDocumentKind === undefined
              ? {}
              : { default_document_kind: defaultDocumentKind }),
          },
        }),
      ),
  );
}

export function useRevokeMailboxSender() {
  return useMailboxMutation<MailboxSenderResponse, string>((senderId) =>
    call(revokeMailboxSender({ path: { sender_id: senderId }, body: { reason: null } })),
  );
}

// --- The audit trail --------------------------------------------------------

/**
 * Everything the viewer may narrow the trail by, as the screen holds it.
 *
 * Blank means "not filtering by this", which is why every field is a string
 * rather than `string | undefined`: these come straight out of form controls,
 * and a control's empty value is `''`. They are translated to absent query
 * parameters below, in one place, so no screen has to remember that an empty
 * `entity_type` would be a filter for entities whose type is the empty string.
 *
 * `companyId` is the exception in kind: the API scopes staff to one company
 * with the `X-Company-Id` header rather than with a query parameter, so it
 * travels as a header -- the same one the portal sends on every request.
 */
export interface AuditEntryFilters {
  readonly action: AuditAction | '';
  readonly entityType: string;
  readonly entityId: string;
  readonly actorId: string;
  readonly correlationId: string;
  /** Inclusive, as the API defines it. An instant, in UTC. */
  readonly occurredFrom: string;
  /** Exclusive, as the API defines it. An instant, in UTC. */
  readonly occurredTo: string;
  readonly companyId: string;
  readonly offset: number;
}

export const NO_AUDIT_FILTERS: AuditEntryFilters = {
  action: '',
  entityType: '',
  entityId: '',
  actorId: '',
  correlationId: '',
  occurredFrom: '',
  occurredTo: '',
  companyId: '',
  offset: 0,
};

/** A filter value the person left blank is not a filter. */
function unlessBlank(value: string): string | undefined {
  return value === '' ? undefined : value;
}

/**
 * One page of the trail, newest first.
 *
 * No `limit` is sent, deliberately. Each entry decrypts a value, so the
 * endpoint's ceiling is low and asking above it is a `422` and no rows at all
 * rather than a shorter page. The server states the size it applied on every
 * response, and the screen pages with that -- so the page size is the API's to
 * choose and there is no number here to drift from it.
 */
export function useAuditEntries(filters: AuditEntryFilters) {
  return useQuery({
    queryKey: QUERY_KEYS.auditEntries(filters),
    queryFn: () =>
      call(
        listAuditEntries({
          query: {
            action: filters.action === '' ? undefined : filters.action,
            entity_type: unlessBlank(filters.entityType),
            entity_id: unlessBlank(filters.entityId),
            actor_id: unlessBlank(filters.actorId),
            correlation_id: unlessBlank(filters.correlationId),
            occurred_from: unlessBlank(filters.occurredFrom),
            occurred_to: unlessBlank(filters.occurredTo),
            ...(filters.offset > 0 && { offset: filters.offset }),
          },
          headers: { 'X-Company-Id': unlessBlank(filters.companyId) },
        }),
      ),
  });
}
